import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Badge from '../../../ui/Badge';
import Card from '../../../ui/Card';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellMoney, CellTag, CellMuted, toneClass } from '../../../ui/DataTable/cells';
import Dialog from '../../../ui/Dialog/Dialog';
import { Field, Input, Checkbox, FormSection } from '../../../ui/Field/Field';
import { toast, confirmDialog, promptDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { parseSheetDate, formatDateDDMMYYYY } from '../utils/dates';
import { ejarCommentsOf, commentsByMonth, commentsTooltip } from '../utils/ejarComments';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import { baseRateFromRows, rentForBilledDays, rentForBilledPeriod, billedEndOf, scheduleFromRows, SHORT_RENT_NOTE } from '../utils/rentRows';
import { contractsService } from '../../../services/firestore';
import { jsPDF } from 'jspdf';
import logger from '../../../utils/logger';
import html2canvas from 'html2canvas';
import { archiveService } from '../../../services/archiveService';
import { finesService, errorMessage } from '../../../services/finesService';
import { fillRowsFromEjar } from '../utils/ejarFill';
import { calculatorSavePayload, openedRateState } from '../utils/calculatorSave';
import { dubaiToday } from '../../../services/contractRules';
import { CONTRACT_CHANGED } from '../../../services/contractVersion';
import { USE_CONTRACTS_V2 } from '../../../services/contractsV2';
import {
    STAFF_SOURCE, contractNoOf, staffChangesOf, saveContractStaff, loadContractStaff, historyAsUpdates,
    notesAsComments, addContractNote, editContractNote, deleteContractNote,
} from '../../../services/contractStaff';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import { storageService } from '../../../services/storageService';
import RowActions from '../ContractsRowActions';
import { phoneDigits, formatPhone } from '../../../utils/phoneUtils';
import { useCompanySettings } from '../../../services/companySettings';
import { withVat, withoutVat } from '../../../constants/finance';
import { contractStatusOf } from '../../../services/contractRules';
import { suggestNextInvoiceNumber, issueInvoiceNumbers, parseInvoiceNumber, formatTypedInvoiceNo, invoiceCounterOn } from '../../../services/invoiceNumbers';
import { dubaiDate, dubaiTime, dubaiDateTime } from '../../../utils/dubaiTime';

const parseFormattedNumber = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return val;
    const cleaned = String(val).replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

// The contract's monthly amount: total_rent first, then the older field names - never Ejar's DAILY
// rental_rate / 'Rental Rate' (owner, 26 Sep 2026; it used to be read when total_rent was empty). The Invygo
// sheet's dealer_monthly_fee is gone with the sheet (owner, 28 Sep 2026) - nothing ever wrote it on a contract.
const monthlyRateOfContract = (contract) => parseFormattedNumber(
    contract.total_rent || contract['Total Rent'] ||
    contract['Rent'] || contract.rent || 0
);

const calculateTotal = (row) => {
    return (parseFloat(row.rent) || 0) + (parseFloat(row.vat) || 0) + (parseFloat(row.salik) || 0) + 
           (parseFloat(row.parking) || 0) + (parseFloat(row.fine) || 0) + (parseFloat(row.other) || 0);
};

const calculateBalance = (row) => {
    return calculateTotal(row) - (parseFloat(row.paid) || 0);
};

// Helper to render text (including Arabic) to a canvas for PDF embedding
const getArabicTextImg = (text, fontSize = 12, bold = false) => {
    if (!text) return null;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Use high resolution for sharp text
    const scale = 4;
    ctx.font = `${bold ? 'bold' : ''} ${fontSize * scale}px Arial, sans-serif`;
    const metrics = ctx.measureText(text);
    
    canvas.width = (metrics.width + 20);
    canvas.height = (fontSize * scale * 1.5);
    
    ctx.font = `${bold ? 'bold' : ''} ${fontSize * scale}px Arial, sans-serif`;
    ctx.fillStyle = 'black';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 10, canvas.height / 2);
    
    return {
        data: canvas.toDataURL('image/png'),
        width: canvas.width / scale,
        height: canvas.height / scale
    };
};

const EditableCell = ({ value, onChange, onBlur, onKeyDown, isEditing, type = "number", className = "", displayClass = "", disabled = false, vatPopup = false }) => {
    const [localValue, setLocalValue] = useState(value === 0 && type === "number" ? "" : String(value ?? ""));
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (isEditing) {
            setLocalValue(value === 0 && type === "number" ? "" : String(value ?? ""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, value]);

    // Close options on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!isEditing || disabled) {
        if (type === "number") {
            return <span className={`${displayClass || 'ds-cell-num'} ${className}`}>{value > 0 ? value.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-'}</span>;
        }
        return <span className={`${displayClass} ${className}`}>{value || '-'}</span>;
    }

    const valNum = parseFloat(localValue) || 0;

    return (
        <div className="relative w-full" ref={containerRef}>
            <input
                type={type === "number" ? "text" : type}
                inputMode={type === "number" ? "decimal" : undefined}
                value={localValue}
                onFocus={() => setIsFocused(true)}
                onChange={(e) => {
                    let newValue = e.target.value;
                    setLocalValue(newValue);
                    
                    if (type === "number") {
                        // Remove commas if pasted with formatting (e.g. 2,532.00)
                        if (newValue.includes(',')) {
                            newValue = newValue.replace(/,/g, '');
                        }
                        
                        if (newValue === "" || newValue === "-") {
                            onChange(0);
                        } else {
                            const parsed = parseFloat(newValue);
                            if (!isNaN(parsed)) {
                                onChange(parsed);
                            }
                        }
                    } else {
                        onChange(newValue);
                    }
                }}
                onBlur={() => {
                    // Small delay to allow click on options if not using preventDefault
                    setTimeout(() => {
                        if (onBlur) onBlur();
                    }, 200);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.target.blur();
                    }
                    if (onKeyDown) onKeyDown(e);
                }}
                placeholder={type === "number" ? "0.00" : "-"}
                autoFocus
                className={`w-full bg-white text-center focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 rounded p-1 border border-gray-300 ${className}`}
            />
            {vatPopup && isFocused && valNum > 0 && (
                <div
                    className="absolute top-full right-0 mt-2 bg-white border border-gray-200 shadow-md rounded-lg p-3 z-[1000] flex flex-col gap-2 min-w-[180px]"
                    onMouseDown={(e) => e.preventDefault()}
                    role="group"
                    aria-label="VAT options"
                >
                    <div className="text-[11px] text-gray-500 font-semibold px-1 border-b border-gray-200 pb-1.5 mb-1 uppercase tracking-wide">VAT Options</div>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            const gross = parseFloat(withVat(valNum).toFixed(2));
                            setLocalValue(gross.toString());
                            onChange(gross);
                            setIsFocused(false);
                        }}
                    >
                        Add 5% VAT (Net)
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setIsFocused(false)}
                    >
                        Already Inclusive
                    </Button>
                </div>
            )}
        </div>
    );
};

const HistoryModal = ({ history, onClose }) => {
    return (
        <Dialog
            open
            onClose={onClose}
            title="Update history"
            size="md"
            bodyClassName="!p-0"
            footer={<Button onClick={onClose}>Close</Button>}
        >
            {history && history.length > 0 ? (
                <div className="divide-y divide-gray-200">
                    {history.map((entry, idx) => (
                        <div key={idx} className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-700 font-semibold text-sm" aria-hidden="true">
                                        {entry.user ? entry.user[0].toUpperCase() : '?'}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-gray-900 leading-none">{entry.user}</span>
                                        <span className="text-[11px] text-gray-500 uppercase mt-1 tracking-wide">{entry.source} Update</span>
                                    </div>
                                </div>
                                <div className="text-right tabular-nums">
                                    <span className="text-xs text-gray-600 block">
                                        {dubaiDate(entry.timestamp)}
                                    </span>
                                    <span className="text-[11px] text-gray-500">
                                        {dubaiTime(entry.timestamp)}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-1.5 pl-11">
                                {entry.changes && entry.changes.map((change, cIdx) => {
                                    const isMonthChange = change.includes('(') || change.includes('Month:');
                                    return (
                                        <div key={cIdx} className="flex items-start gap-2">
                                            <div className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-none ${isMonthChange ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
                                            <span className={`text-sm leading-relaxed ${isMonthChange ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                                                {change}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500 flex flex-col items-center gap-3">
                    <Icon glyph={ICONS.history} size="lg" tone="muted" />
                    <p className="text-sm text-gray-600">No audit history found</p>
                </div>
            )}
        </Dialog>
    );
};

// --- Sub-components for Sidebar & Modals ---


// Sub-components are now defined at the bottom of the file for better organization.


// Rate schedule editor: "from <date> the monthly rate is <rate>". Learned from the
// saved rows the first time, then a dated fact you edit here - not a hand-edited row.
const RateScheduleView = ({ schedule, setSchedule, rows, setRows, setIsDirty, baseRate, readOnly = false }) => {
    const [draft, setDraft] = useState({ from: '', rate: '', note: '' });
    const [applyFrom, setApplyFrom] = useState('');
    const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const toSheet = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
    const toIso = (sheet) => { const m = String(sheet || '').match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
    const entries = [...(schedule || [])].sort((a, b) => toIso(a.from).localeCompare(toIso(b.from)));

    const add = () => {
        if (!draft.from || !(parseFloat(draft.rate) > 0)) return;
        setSchedule([...(schedule || []), { from: toSheet(draft.from), rate: parseFloat(draft.rate), note: draft.note || '', source: 'manual' }]);
        setDraft({ from: '', rate: '', note: '' }); setIsDirty(true);
    };
    const remove = (i) => { const e = entries[i]; setSchedule((schedule || []).filter((x) => x !== e)); setIsDirty(true); };
    const relearn = () => { setSchedule(scheduleFromRows(rows, baseRate)); setIsDirty(true); };

    // Re-price rows starting on/after a date from the schedule (rent / VAT / total).
    const preview = React.useMemo(() => {
        if (!applyFrom) return [];
        const fromSheet = toSheet(applyFrom);
        return rows.map((r, idx) => {
            if (toIso(r.start) < toIso(fromSheet)) return null;
            const priced = rentForBilledPeriod({ start: r.start, billEnd: r.end, schedule, fallbackBase: baseRate, first: idx === 0 });
            const cur = parseFloat(r.totalRent) || 0;
            return Math.abs(priced.total - cur) > 0.005 ? { idx, start: r.start, end: r.end, from: cur, to: priced.total, parts: priced.parts, split: priced.split } : null;
        }).filter(Boolean);
    }, [applyFrom, rows, schedule, baseRate]);
    const apply = () => {
        if (!preview.length) return;
        const next = rows.map((r) => ({ ...r }));
        for (const p of preview) {
            const rent = parseFloat(withoutVat(p.to).toFixed(2));
            next[p.idx] = { ...next[p.idx], totalRent: p.to, rent, vat: parseFloat((p.to - rent).toFixed(2)),
                            rateNote: p.split ? p.parts.map((x) => `${x.days}d × ${fmt(x.rate)}`).join(' + ') : '' };
        }
        setRows(next); setIsDirty(true); setApplyFrom('');
    };

    return (
        <div className="flex flex-col gap-4 text-sm text-gray-900">
            <p className="text-xs text-gray-600">The monthly rate in force from each date. Every month is priced from this table with the usual rules (30+ days or the first month = full month at the rate in force at its end; shorter = rate ÷ 30 × days, per part when the rate changes inside it).</p>
            <TableContainer>
                <Table>
                    <TableHead><TableRow>
                        <TableHeader>From</TableHeader><TableHeader>Rate / month</TableHeader><TableHeader>Note</TableHeader><TableHeader><span className="sr-only">Actions</span></TableHeader></TableRow></TableHead>
                    <TableBody>
                        {entries.length === 0 && <TableRow><TableCell colSpan={4}><CellMuted>no rate yet</CellMuted></TableCell></TableRow>}
                        {entries.map((e, i) => (
                            <TableRow key={i}>
                                <TableCell><span className="ds-cell-date">{e.from || '(start)'}</span></TableCell>
                                <TableCell><CellMoney value={e.rate || 0} /></TableCell>
                                <TableCell>{e.note ? <CellText>{e.note}</CellText> : null}{e.source === 'learned' ? <span className="ml-1"><CellTag tone="warning">learned</CellTag></span> : null}</TableCell>
                                <TableCell>{!readOnly && <Button variant="ghost" size="xs" className="text-red-700" onClick={() => remove(i)}>remove</Button>}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!readOnly && <>
            <div className="flex flex-wrap items-end gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">From<input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="block mt-1 border border-gray-300 rounded-lg px-2 py-1 text-sm font-normal normal-case text-gray-900" /></label>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Rate / month<input type="number" min="0" step="1" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} className="block mt-1 w-28 border border-gray-300 rounded-lg px-2 py-1 text-sm font-normal normal-case text-gray-900" /></label>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex-1 min-w-[120px]">Reason<input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="renewal / offer / …" className="block mt-1 w-full border border-gray-300 rounded-lg px-2 py-1 text-sm font-normal normal-case text-gray-900" /></label>
                <Button variant="primary" size="sm" onClick={add}>Add change</Button>
                <Button variant="link" size="sm" onClick={relearn}>re-learn from rows</Button>
            </div>
            <Card padding="sm"><div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-end gap-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Re-price rows starting from<input type="date" value={applyFrom} onChange={(e) => setApplyFrom(e.target.value)} className="block mt-1 border border-gray-300 rounded-lg px-2 py-1 text-sm font-normal normal-case text-gray-900" /></label>
                    <Button size="sm" onClick={apply} disabled={!preview.length}>Apply to {preview.length} row(s)</Button>
                </div>
                {applyFrom && preview.length === 0 && <div className="text-xs text-green-700 font-medium flex items-center gap-1"><Icon glyph={ICONS.check} size="xs" /> all rows from that date already match the schedule</div>}
                {preview.map((p) => (
                    <div key={p.idx} className="text-xs text-gray-900 flex flex-wrap gap-2 border-t border-gray-100 pt-1 tabular-nums">
                        <span className="font-mono">{p.start} → {p.end}</span><span>{fmt(p.from)} → <b>{fmt(p.to)}</b></span>
                        {p.split && <span className="text-amber-700">{p.parts.map((x) => `${x.days}d × ${fmt(x.rate)}`).join(' + ')}</span>}
                    </div>
                ))}
            </div></Card>
            </>}
        </div>
    );
};

// The version a Save names: the contracts store's staff_version (REACT_APP_CONTRACTS_V2 on: GET .../staff), else the
// Firestore document's last_updated (CON-03).
const openedVersionOf = (contract) => (USE_CONTRACTS_V2 ? contract._staffVersion : (contract._last_updated ?? ''));

// `onReload` (the contracts store only): open this contract again from the store - after a save was refused because
// someone saved it since (409), the calculator shows the latest months.
const MonthlyCalculator = ({ contract, onClose, onSaveSuccess, switchBackOf, onCopy, onReload }) => {
    const { currentUser } = useAuth();
    const { settings: companySettings } = useCompanySettings();
    // through usePermissions (permissions phase 3, 4 Oct 2026): "show the app as this role" shows the role's buttons
    // here too, and a new permission follows today's rule until the profile is upgraded
    const { hasPermission } = usePermissions();
    // What this user may change - the engine's own gates (engine/api/contract_staff.py): nobody without a staff-edit
    // permission saves at all, and each column / register / button follows its group (owner, 3 Oct 2026)
    const canSave = hasPermission(PERMISSIONS.edit_contracts) || hasPermission(PERMISSIONS.write_contracts);
    const may = (p) => canSave && hasPermission(p);        // a column's own permission, and a way to keep the change
    const canAccounts = may(PERMISSIONS.edit_monthly_accounts);
    const canOps = may(PERMISSIONS.edit_monthly_ops);

    const modalRef = useRef(null);
    const sidebarRef = useRef(null);
    const [rows, setRows] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [editingIdx, setEditingIdx] = useState(-1);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showProportional, setShowProportional] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [isUploading, setIsUploading] = useState(null); // idx of uploading row
    const [showHistory, setShowHistory] = useState(false);
    const [invoiceCompanyName, setInvoiceCompanyName] = useState(contract.invoiceCompanyName || '');
    const [invoiceCompanyAddress, setInvoiceCompanyAddress] = useState(contract.invoiceCompanyAddress || '');
    const [invoiceCompanyTRN, setInvoiceCompanyTRN] = useState(contract.invoiceCompanyTRN || '');
    const [invoiceKMLimit, setInvoiceKMLimit] = useState(contract.invoiceKMLimit || 2500);
    const [comments, setComments] = useState(contract.comments || []);
    const [newCommentText, setNewCommentText] = useState('');
    const [showSidebar, setShowSidebar] = useState(false);
    const [activeSidebarTab, setActiveSidebarTab] = useState('addons');
    const [, setIsClosedContract] = useState(false);
    // The contract version (last_updated) the month rows were opened from. Save sends it and is
    // refused when the document changed since (a Fill from Ejar, another user) - CON-03. Our own
    // saves move it forward.
    // (the contracts store: the staff_version the months were opened with - its notes are saved one by one and
    // never move it)
    const versionRef = useRef(openedVersionOf(contract));
    useEffect(() => { versionRef.current = openedVersionOf(contract); }, [contract]);
    // the contracts store: the History as the store keeps it (refreshed after a save and when it is opened)
    const [storeHistory, setStoreHistory] = useState(contract.updateHistory || []);
    // rate + schedule as opened: Save writes them only when the user changed them (CON-02)
    const openedRateRef = useRef(openedRateState(0, []));

    // Sidebar Close on Click Outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showSidebar && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                // Clicks inside another dialog opened from the panel (edit/delete comment prompts)
                // must not close it, as the old native prompt/confirm did not.
                const ownRoot = modalRef.current && modalRef.current.closest('.ds-dialog-root');
                const clickRoot = event.target.closest && event.target.closest('.ds-dialog-root');
                if (clickRoot && clickRoot !== ownRoot) return;
                // If it's not a trigger button, close it
                if (!event.target.closest('.sidebar-trigger')) {
                    setShowSidebar(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showSidebar]);
    const [addons, setAddons] = useState(contract.addons || {
        fullInsurance: false,
        additionalDriver: false,
        extraKM: 0
    });
    const [showFinesModal, setShowFinesModal] = useState(false);
    const [currentFineRowIndex, setCurrentFineRowIndex] = useState(null);
    const [showOtherChargesModal, setShowOtherChargesModal] = useState(false);
    const [currentOtherRowIndex, setCurrentOtherRowIndex] = useState(null);
    const [showParkingModal, setShowParkingModal] = useState(false);
    const [currentParkingRowIndex, setCurrentParkingRowIndex] = useState(null);
    const [copyFeedback, setCopyFeedback] = useState(false);
    const [salikVatModalData, setSalikVatModalData] = useState(null); // { idx, amount }
    const [isFilling, setIsFilling] = useState(false);
    const [fillSummary, setFillSummary] = useState(null); // banner after a Fill-from-Ejar run

    const bookingValStr = String(contract.bookingNo || contract.booking || '').toLowerCase().trim();
    const contractNoStr = String(contract.contractNo || contract.contract || '').toLowerCase().trim();
    const isLeasingCustomer = contract.type === 'Leasing' || contract.category === 'Leasing' || 
                              bookingValStr.startsWith('c') || bookingValStr.includes('leasing') || 
                              (!bookingValStr && contractNoStr.startsWith('c'));

    const autoSaveComments = async (newCommentsArray) => {
        try {
            const updatedBy = (currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'Unknown User'));
            // A comment is saved even when the contract changed meanwhile - but only an unchanged one
            // moves this calculator's version forward, so its month rows stay refused (CON-03).
            try {
                const res = await contractsService.updateContract(contract.id, {
                    comments: newCommentsArray
                }, updatedBy, 'Comment Update', { expectedLastUpdated: versionRef.current });
                versionRef.current = res.last_updated;
            } catch (err) {
                if (err.code !== CONTRACT_CHANGED) throw err;
                await contractsService.updateContract(contract.id, { comments: newCommentsArray }, updatedBy, 'Comment Update');
            }
            
            if (onSaveSuccess) {
                onSaveSuccess([{ ...contract, comments: newCommentsArray }]);
            }
        } catch (error) {
            logger.error('Failed to auto-save comments:', error);
            toast.error("Could not save. Please try again.");
        }
    };

    // The contracts store: a note is saved on its own (POST / PATCH / DELETE .../staff/notes) - two users adding one at
    // the same moment never overwrite each other, and the months' Save never carries them. The list shown is the
    // store's answer; the latest list is kept in a ref, as the answers arrive after the dialogs close.
    const commentsRef = useRef(comments);
    commentsRef.current = comments;
    const showStoreNotes = (next) => {
        setComments(next);
        // the page's row takes the notes only - never the months as they were when this calculator opened
        if (onSaveSuccess) onSaveSuccess([{ id: contract.id, contractNo: contract.contractNo, comments: next }]);
    };
    const addStoreNote = async (text) => {
        setNewCommentText('');
        try {
            const [note] = notesAsComments([await addContractNote(contractNoOf(contract), text)]);
            showStoreNotes([...commentsRef.current, note]);
        } catch (err) {
            setNewCommentText(text);                 // typed again only if it was not saved
            toast.error(err.message);
        }
    };
    const editStoreNote = async (id, text) => {
        try {
            const [note] = notesAsComments([await editContractNote(contractNoOf(contract), id, text)]);
            showStoreNotes(commentsRef.current.map((c) => (c.id === id ? note : c)));
        } catch (err) {
            toast.error(err.message);
        }
    };
    const deleteStoreNote = async (id) => {
        try {
            await deleteContractNote(contractNoOf(contract), id);
            showStoreNotes(commentsRef.current.filter((c) => c.id !== id));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleAddComment = () => {
        if (!newCommentText.trim()) return;
        if (USE_CONTRACTS_V2) { addStoreNote(newCommentText.trim()); return; }
        const author = currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'Unknown User');
        const newComment = {
            id: Date.now().toString(),
            text: newCommentText.trim(),
            user: author,
            timestamp: new Date().toISOString()
        };
        const newArray = [...comments, newComment];
        setComments(newArray);
        setNewCommentText('');
        setIsDirty(true);
        autoSaveComments(newArray);
    };

    const handleEditComment = async (id) => {
        if (!may('monthly_edit_comments')) return;      // its own permission (phase 3; was manage_users)
        const comment = comments.find(c => c.id === id);
        if (!comment) return;
        const newText = await promptDialog({ title: "Edit comment", label: "Comment", initialValue: comment.text });
        if (newText !== null && newText.trim() !== '' && USE_CONTRACTS_V2) {
            await editStoreNote(id, newText.trim());
        } else if (newText !== null && newText.trim() !== '') {
            const newArray = comments.map(c => c.id === id ? { ...c, text: newText.trim(), editedAt: new Date().toISOString() } : c);
            setComments(newArray);
            setIsDirty(true);
            autoSaveComments(newArray);
        }
    };

    const handleDeleteComment = async (id) => {
        if (!may('monthly_edit_comments')) return;
        if (await confirmDialog({ title: "Delete comment?", message: "Are you sure you want to delete this comment?", confirmLabel: "Delete", danger: true })) {
            if (USE_CONTRACTS_V2) { await deleteStoreNote(id); return; }
            const newArray = comments.filter(c => c.id !== id);
            setComments(newArray);
            setIsDirty(true);
            autoSaveComments(newArray);
        }
    };
    const [historyPickupKM, setHistoryPickupKM] = useState(0);
    // Enhanced lookup for pickup KM
    const pickupKM = historyPickupKM || parseFloat(
        contract.pickupKM || 
        contract['Pick-up KM'] || 
        contract['Odometer'] || 
        contract['KM Out'] || 
        contract['ODO'] || 
        0
    );
    // The Dubai calendar day (at UTC midnight), whatever the browser's clock zone (owner, 26 Sep 2026).
    const today = dubaiToday();
    const [simulationDate, setSimulationDate] = useState(today);
    const [manualMonthlyRate, setManualMonthlyRate] = useState(0);
    // Rate schedule: saved with the contract; learned from the rows the first time.
    const [rateSchedule, setRateSchedule] = useState(Array.isArray(contract?.rate_schedule) ? contract.rate_schedule : null);

    const adjustSimulationDate = (days) => {
        const newDate = new Date(simulationDate);
        newDate.setUTCDate(newDate.getUTCDate() + days);
        setSimulationDate(newDate);
    };

    // Resolves the base rate for a row and delegates the maths to the shared rentForBilledDays() helper
    // (utils/rentRows.js; the engine's twin contract_staff.rent_for_billed_days): a row runs from its start up to its
    // end, the day the next month starts (or a returned contract's billed end), so that day is counted once - 30+ days
    // = the full month, shorter = rate / 30 x days, and the FIRST row (idx 0) is always the full monthly amount (owner,
    // 26 Sep 2026). With a rate schedule each period is priced at the rate in force in it.
    // `maxDate` ("Today Usage"): the rent used up to that day if the car came back then - a month not started yet
    // costs nothing, the month running is cut there, an earlier one keeps its own amount.
    const getProportionalRent = (row, idx, baseRateArg, currentRows, maxDate, scheduleArg) => {
        const sourceRows = currentRows || rows;
        // Use contract.total_rent as the ultimate source of truth if baseRateArg is missing
        const fallbackBase = parseFloat(contract?.total_rent || contract?.rent || sourceRows[0]?.totalRent || 0);
        const baseRate = baseRateArg !== undefined ? baseRateArg : fallbackBase;
        const first = idx === 0;
        const schedule = scheduleArg !== undefined ? scheduleArg : rateSchedule;
        let billEnd = row.end;
        if (maxDate) {
            const start = parseSheetDate(String(row.start || ''));
            const end = parseSheetDate(String(row.end || ''));
            const limit = new Date(maxDate);
            limit.setUTCHours(0, 0, 0, 0);
            if (!start || !end) return parseFloat(row.totalRent) || 0;
            if (start >= limit && !first) return 0;
            if (end <= limit) return parseFloat(row.totalRent) || 0;
            billEnd = limit;
        }
        return rentForBilledDays({ start: row.start, billEnd, schedule, fallbackBase: baseRate, first });
    };

    // The header "Monthly Rate" is the rate in force NOW: editing it changes the
    // schedule's latest entry only, so earlier months keep their own rate.
    const scheduleWithCurrentRate = (rate) => {
        if (!rateSchedule || rateSchedule.length === 0 || !(rate > 0)) return rateSchedule;
        const sorted = [...rateSchedule].sort((a, b) => String(parseSheetDate(a.from)?.getTime() || 0).localeCompare(String(parseSheetDate(b.from)?.getTime() || 0)));
        const last = sorted[sorted.length - 1];
        if (Math.abs(parseFloat(last.rate) - rate) < 0.005) return rateSchedule;
        return rateSchedule.map((e) => (e === last ? { ...e, rate } : e));
    };

    useEffect(() => {
        // a contract switched while its slow reads run: the older contract's rows never land on the newer one (review 18 Oct 2026)
        let alive = true;
        const guard = (fn) => (...a) => { if (alive) fn(...a); };
        const live = { setHistoryPickupKM: guard(setHistoryPickupKM), setIsClosedContract: guard(setIsClosedContract),
            setIsDirty: guard(setIsDirty), setIsLoadingData: guard(setIsLoadingData), setManualMonthlyRate: guard(setManualMonthlyRate),
            setRateSchedule: guard(setRateSchedule), setRows: guard(setRows) };
        setLoadError('');
        const loadData = async () => {
            try {
                live.setIsLoadingData(true);
                const statusRaw = contractStatusOf(contract).toLowerCase();   // blank = unknown, not open
                const typeValue = (contract.type || '').toString().toLowerCase();
                const isClosed = statusRaw !== 'open' || typeValue.startsWith('closed');
                live.setIsClosedContract(isClosed);

                // Initial rows sync
                let initialRows = contract.monthly_calculations || [];

                // Determine baseRate for pre-filling/correcting rows.
                // The monthly amount: total_rent first; never Ejar's DAILY rental_rate (owner, 26 Sep 2026).
                let baseRate = monthlyRateOfContract(contract);

                // If official rate is missing, fallback to first row but back-calculate to 30 days
                // (shared with the bulk sync via baseRateFromRows in utils/rentRows.js)
                if (baseRate === 0 && initialRows.length > 0) {
                    baseRate = baseRateFromRows(initialRows);
                }

                // Rate schedule: saved with the contract, else LEARNED from the saved
                // rows before any new month is generated - so a contract whose price
                // moved (3000 then 2400) keeps 3000 for the old months and generates
                // the new ones at 2400. The header rate becomes the rate in force now.
                const schedule = Array.isArray(contract.rate_schedule) && contract.rate_schedule.length > 0
                    ? contract.rate_schedule
                    : scheduleFromRows(initialRows, baseRate);
                live.setRateSchedule(schedule);
                if (schedule.length > 0) {
                    const latest = [...schedule].sort((a, b) => (parseSheetDate(a.from)?.getTime() || 0) - (parseSheetDate(b.from)?.getTime() || 0)).pop();
                    if (parseFloat(latest.rate) > 0) baseRate = parseFloat(latest.rate);
                }
                live.setManualMonthlyRate(baseRate);
                openedRateRef.current = openedRateState(baseRate, schedule);

                const dropoffDateStr = contract.dropoffDate || contract['Drop-off Date'];
                const dropoffDateObj = parseSheetDate(dropoffDateStr);
                if (dropoffDateObj) dropoffDateObj.setUTCHours(0, 0, 0, 0);

                const bookingNumber = contract['Booking Number'] || 
                                    contract['Booking No'] || 
                                    contract['Booking ID'] || 
                                    contract['Agreement'] || 
                                    contract['Contract No.'] || 
                                    contract['Contract No'] || 
                                    contract.id;

                // --- FETCH MILEAGE HISTORY (Optimization: Skip if already known) ---
                const existingPickup = parseFloat(contract.pickupKM || contract['Pick-up KM'] || 0);
                
                if (existingPickup > 0) {
                    live.setHistoryPickupKM(existingPickup);
                } else if (bookingNumber) {
                    try {
                        const history = await contractsService.getContractsByBookingNumber(bookingNumber);
                        const contractNumbers = history.map(c => c['Contract No.'] || c['Contract No'] || c['Agreement']).filter(Boolean);
                        const [byBooking, byContract] = await Promise.all([
                            archiveService.findByBookingNumber(bookingNumber),
                            archiveService.findByContractNumbers([...contractNumbers, bookingNumber])
                        ]);

                        const combinedLogs = [...byBooking, ...byContract];
                        const seen = new Set();
                        const deduplicatedLogs = combinedLogs.filter(r => {
                            // the check type too: a Create and a Close in the same minute are two checks, not one (CC 79361)
                            const key = `${r['Contract No.'] || r['Contract No']}|${r['Plate Number'] || r['Plate No.']}|${r['Transaction Date']}|${r['Check Type'] || ''}`;
                            if (!seen.has(key)) { seen.add(key); return true; }
                            return false;
                        });

                        if (deduplicatedLogs.length > 0) {
                            const openLogs = deduplicatedLogs.filter(l => {
                                const type = (l['Check Type'] || '').toLowerCase();
                                return type.includes('open') || type.includes('create') || type.includes('pickup');
                            }).sort((a, b) => (parseSheetDate(a['Transaction Date']) || new Date(0)) - (parseSheetDate(b['Transaction Date']) || new Date(0)));

                            if (openLogs.length > 0) {
                                const odo = parseFloat(openLogs[0]['Odometer'] || openLogs[0]['ODO'] || openLogs[0]['KM'] || 0);
                                if (odo > 0) live.setHistoryPickupKM(odo);
                            } else {
                                const sortedAny = [...deduplicatedLogs].sort((a, b) => (parseSheetDate(a['Transaction Date'] || a['Pick-up Date']) || new Date(0)) - (parseSheetDate(b['Transaction Date'] || b['Pick-up Date']) || new Date(0)));
                                const odo = parseFloat(sortedAny[0]['Odometer'] || sortedAny[0]['ODO'] || sortedAny[0]['KM'] || 0);
                                if (odo > 0) live.setHistoryPickupKM(odo);
                            }
                        } else if (history && history.length > 0) {
                            const sortedHistory = [...history].sort((a, b) => (parseSheetDate(a['Pick-up Date']) || new Date(0)) - (parseSheetDate(b['Pick-up Date']) || new Date(0)));
                            for (const segment of sortedHistory) {
                                const odo = parseFloat(segment['Odometer'] || segment['ODO'] || segment['KM'] || 0);
                                if (odo > 0) { live.setHistoryPickupKM(odo); break; }
                            }
                        }
                    } catch (historyErr) {
                        logger.error('Error fetching mileage history:', historyErr);
                    }
                }

                const pickupDateObj = parseSheetDate(contract.pickupDate || contract['Pick-up Date']);
                const shouldClamp = isClosed;
                // A returned contract is billed up to the pick-up day + Ejar's own Trip Days (owner, 14 Oct 2026 - its
                // grace and extra-hour rules decide the days, the calculator only the price), else up to its drop-off
                // day; the limit is the day AFTER the last billed day, so the boundary day is never counted twice.
                const billedEnd = shouldClamp
                    ? billedEndOf({ pickup: pickupDateObj, dropoff: dropoffDateObj, ejarDays: contract.ejar_days })
                    : null;
                const limitDate = new Date((shouldClamp ? (billedEnd || today) : today).getTime());
                limitDate.setUTCHours(0, 0, 0, 0);

                let modified = false;

                // 1. Load existing calculations if they exist
                if (contract.monthly_calculations && Array.isArray(contract.monthly_calculations) && contract.monthly_calculations.length > 0) {
                    initialRows = [...contract.monthly_calculations];
                }
                
                // 2. Perform shared Transition 30-day date normalization if pickupDate is known
                if (pickupDateObj && initialRows.length > 0) {
                    let nextExpectedStart = new Date(pickupDateObj);
                    nextExpectedStart.setUTCHours(0, 0, 0, 0);

                    initialRows = initialRows.map((r) => {
                        // Respect existing dates if they are valid
                        const savedStartStr = r.start;
                        const savedEndStr = r.end;
                        const savedStart = parseSheetDate(savedStartStr);
                        const savedEnd = parseSheetDate(savedEndStr);

                        let finalStartStr = savedStartStr;
                        let finalEndStr = savedEndStr;

                        if (!savedStart || !savedEnd) {
                            // Only generate new dates if missing
                            const start = new Date(nextExpectedStart);
                            const end = new Date(start);
                            end.setUTCDate(end.getUTCDate() + 30);
                            finalStartStr = formatDateDDMMYYYY(start);
                            finalEndStr = formatDateDDMMYYYY(end);
                            modified = true;
                        }

                        const row = {
                            ...r,
                            start: finalStartStr,
                            end: finalEndStr,
                            totalRent: r.totalRent || parseFloat(((parseFloat(r.rent) || 0) + (parseFloat(r.vat) || 0)).toFixed(2))
                        };
                        
                        // Update next expected start for new rows
                        const currentEndObj = parseSheetDate(finalEndStr);
                        if (currentEndObj) {
                            nextExpectedStart = new Date(currentEndObj);
                        }
                        
                        return row;
                    });
                } else if (initialRows.length > 0) {
                    // If no pickup date to normalize with, just ensure totalRent is present
                    initialRows = initialRows.map(r => ({
                        ...r,
                        totalRent: r.totalRent || parseFloat(((parseFloat(r.rent) || 0) + (parseFloat(r.vat) || 0)).toFixed(2))
                    }));
                }

                // We no longer force mathematical alignment on load to allow manual overrides 
                // like manually correcting a 29-day month to full rent to persist.

                // 3. Extension: Add missing months until limitDate is reached
                if (pickupDateObj) {
                    let currentStart;
                    if (initialRows.length > 0) {
                        // Start from where the last row ended
                        const lastRow = initialRows[initialRows.length - 1];
                        currentStart = parseSheetDate(lastRow.end);
                    } else {
                        // Start from pickup date
                        currentStart = new Date(pickupDateObj);
                    }

                    if (currentStart) {
                        currentStart.setUTCHours(0, 0, 0, 0);
                        
                        // Determine baseRate for pre-filling/correcting rows. 
                        // CRITICAL: Prioritize manual weight if entered, else contract's official rate.
                        const baseRate = manualMonthlyRate || monthlyRateOfContract(contract);

                        while (true) {
                            // Stop if we reached or passed the limitDate
                            if (currentStart.getTime() >= limitDate.getTime() && initialRows.length > 0) break;

                            const end = new Date(currentStart);
                            end.setUTCDate(end.getUTCDate() + 30);
                            
                            const isEarlyReturn = shouldClamp && end > limitDate;
                            const finalEndDate = isEarlyReturn ? limitDate : end;

                            // Prevent adding empty/zero-day rows
                            if (currentStart.getTime() === finalEndDate.getTime()) break;

                            // Pre-fill rent if baseRate exists
                            let newTotalRent = 0;
                            let newRent = 0;
                            let newVat = 0;
                            let rowNotes = '';
                            
                            // no rate at all: a short contract's FIRST month is Ejar's own rent for its days (owner,
                            // 14 Oct 2026; contract_staff.calculator_open twin)
                            const shortRent = initialRows.length === 0 && !(baseRate > 0) && schedule.length === 0
                                ? (Number(contract.ejar_short_rent) || 0) : 0;
                            if (baseRate > 0 || shortRent > 0) {
                                const tempRow = { start: formatDateDDMMYYYY(currentStart), end: formatDateDDMMYYYY(finalEndDate) };
                                newTotalRent = baseRate > 0
                                    ? getProportionalRent(tempRow, initialRows.length, baseRate, initialRows, undefined, schedule)
                                    : shortRent;
                                newRent = parseFloat(withoutVat(newTotalRent).toFixed(2));
                                newVat = parseFloat((newTotalRent - newRent).toFixed(2));

                                if (isEarlyReturn) {
                                    rowNotes = 'Early Return';
                                }
                            }

                            initialRows.push({
                                start: formatDateDDMMYYYY(currentStart),
                                end: formatDateDDMMYYYY(finalEndDate),
                                totalRent: newTotalRent, rent: newRent, vat: newVat,
                                salik: 0, parking: 0, fine: 0, other: 0, paid: 0, date: '-', invNo: rowNotes || '',
                                currentKM: 0,
                                isEarlyReturn: isEarlyReturn,
                                // Ejar's rent for its days: never learned from as a monthly rate (rentRows)
                                ...(!(baseRate > 0) && shortRent > 0 ? { rateNote: SHORT_RENT_NOTE } : {})
                            });
                            
                            modified = true;
                            
                            if (currentStart.getTime() >= limitDate.getTime()) break;
                            currentStart = end;
                        }
                    }
                }

                // Dedulicate and cleanup zero-day rows that might have been saved before
                if (initialRows.length > 0) {
                    const originalLength = initialRows.length;
                    initialRows = initialRows.filter((r, idx) => {
                        if (idx === 0) return true; // Keep first row always
                        // never one holding a payment or a charge (a returned contract's month starting on its billed
                        // end keeps its Salik / fines - review, 14 Oct 2026; contract_staff.calculator_open twin)
                        return r.start !== r.end || (parseFloat(r.paid) || 0) > 0
                            || ['salik', 'parking', 'fine', 'other'].some((f) => (parseFloat(r[f]) || 0) !== 0);
                    });
                    if (initialRows.length !== originalLength) modified = true;
                }

                if (initialRows.length > 0 && shouldClamp) {
                    const clampBaseRate = manualMonthlyRate || baseRate;

                    const filteredRows = initialRows.filter((r, idx) => {
                        const rowStart = parseSheetDate(r.start);
                        if (!rowStart || idx === 0) return true;      // the first month is never dropped
                        rowStart.setUTCHours(0, 0, 0, 0);
                        
                        const hasData = (parseFloat(r.paid) || 0) !== 0 || 
                                        (parseFloat(r.salik) || 0) !== 0 || 
                                        (parseFloat(r.parking) || 0) !== 0 || 
                                        (parseFloat(r.fine) || 0) !== 0 || 
                                        (parseFloat(r.other) || 0) !== 0 ||
                                        (r.invNo && r.invNo !== '') ||
                                        (parseFloat(r.currentKM) || 0) !== 0;

                        // starting on or after the billed end: not one day of it is billed
                        const isAfter = rowStart >= limitDate;
                        if (isAfter && !hasData) {
                            modified = true;
                            return false;
                        }
                        return true;
                    });

                    if (filteredRows.length > 0) {
                        const lastRow = filteredRows[filteredRows.length - 1];
                        const lastEnd = parseSheetDate(lastRow.end);
                        
                        let needsUpdate = false;
                        if (lastEnd && (lastEnd > limitDate || lastEnd.getTime() !== limitDate.getTime())) {
                            lastRow.end = formatDateDDMMYYYY(limitDate);
                            lastRow.isEarlyReturn = true;
                            if (!lastRow.invNo || lastRow.invNo === '') {
                                lastRow.invNo = 'Early Return';
                            }
                            needsUpdate = true;
                        }
                        
                        if (clampBaseRate > 0) {
                            const expectedTotal = getProportionalRent(lastRow, filteredRows.length - 1, clampBaseRate, filteredRows, undefined, schedule);
                            const currentTotal = parseFloat(lastRow.totalRent) || 0;
                            
                            // Only overwrite if it differs
                            if (Math.abs(currentTotal - expectedTotal) > 0.01) {
                                lastRow.totalRent = expectedTotal;
                                const pRent = parseFloat(withoutVat(expectedTotal).toFixed(2));
                                lastRow.rent = pRent;
                                lastRow.vat = parseFloat((expectedTotal - pRent).toFixed(2));
                                needsUpdate = true;
                            }
                        }
                        if (needsUpdate) modified = true;
                    }

                    if (modified) live.setIsDirty(true);
                    live.setRows(filteredRows);
                } else {
                    if (modified) live.setIsDirty(true);
                    live.setRows(initialRows);
                }
            } catch (err) {
                logger.error('Error in loadData:', err);
                if (alive) setLoadError(err?.message || 'The contract could not be loaded');
            } finally {
                live.setIsLoadingData(false);
            }
        };
        loadData();
        return () => { alive = false; };
        // Loads the rows once per contract. today / getProportionalRent are rebuilt every render and
        // manualMonthlyRate is typed by the user, so listing them would reload and overwrite the rows.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract]);

    // Escape is handled by the Dialog (top-most dialog only) and goes through requestClose below.

    const handleCellChange = (idx, fieldOrUpdates, value) => {
        setRows(prevRows => {
            const newRows = [...prevRows];
            const updates = typeof fieldOrUpdates === 'object' ? fieldOrUpdates : { [fieldOrUpdates]: value };
            
            newRows[idx] = { ...newRows[idx], ...updates };
            
            for (const field in updates) {
                const val = parseFloat(updates[field]) || 0;
                
                if (field === 'totalRent' || field === 'rent') {
                    let totalVal = field === 'totalRent' ? val : parseFloat(withVat(val).toFixed(2));
                    let rentVal = field === 'rent' ? val : parseFloat(withoutVat(val).toFixed(2));
                    let vatVal = parseFloat((totalVal - rentVal).toFixed(2));

                    newRows[idx].rent = rentVal;
                    newRows[idx].vat = vatVal;
                    newRows[idx].totalRent = totalVal;

                } else if (field === 'start' || field === 'end') {
                    // When dates change, optionally recalculate price
                    const baseRate = parseFloat(contract.total_rent || contract.rent || newRows[0]?.totalRent || 0);
                    if (baseRate > 0) {
                        const propVal = getProportionalRent(newRows[idx], idx, baseRate, newRows);
                        newRows[idx].totalRent = propVal;
                        const pRent = parseFloat(withoutVat(propVal).toFixed(2));
                        newRows[idx].rent = pRent;
                        newRows[idx].vat = parseFloat((propVal - pRent).toFixed(2));
                    }
                } else if (field === 'vat') {
                    newRows[idx].totalRent = parseFloat(((parseFloat(newRows[idx].rent) || 0) + val).toFixed(2));
                } else if (field === 'paid') {
                    if (!newRows[idx].date || newRows[idx].date === '-') {
                        newRows[idx].date = formatDateDDMMYYYY(today);
                    }
                } else if (field === 'salik') {
                    // Reset VAT state if number is cleared or set to 0
                    if (val === 0) {
                        newRows[idx].salikHasVat = false;
                    }
                } else if (field === 'salikTo') {
                    // Start propagation from the current row to the end of the table
                    for (let i = idx; i < newRows.length - 1; i++) {
                        const currentTo = parseSheetDate(newRows[i].salikTo);
                        if (currentTo && !isNaN(currentTo.getTime())) {
                            const nextFrom = new Date(currentTo);
                            nextFrom.setUTCDate(nextFrom.getUTCDate() + 1);
                            
                            const nextFromStr = formatDateDDMMYYYY(nextFrom);
                            // Only update if it's different to avoid unnecessary state changes
                            if (newRows[i + 1].salikFrom !== nextFromStr) {
                                newRows[i + 1] = { 
                                    ...newRows[i + 1], 
                                    salikFrom: nextFromStr 
                                };
                            }
                        } else {
                            // If we hit a row without a valid end date, stop propagation
                            break;
                        }
                    }
                }
            }

            return newRows;
        });
        setIsDirty(true);
    };

    const handleFileUpload = async (idx, file) => {
        if (!canOps) return;
        if (!file) return;
        setIsUploading(idx);
        try {
            const contractId = contract.contractNo || contract.id;
            const row = rows[idx];
            const fileName = `${contractId}_${row.start.replace(/\//g, '-')}_dashboard_${Date.now()}.jpg`;
            const path = `dashboards/${contractId}/${fileName}`;
            
            const downloadURL = await storageService.uploadFile(file, path);
            
            const newRows = [...rows];
            newRows[idx] = { ...newRows[idx], dashboardImage: downloadURL };
            setRows(newRows);
            setIsDirty(true);
            logger.info('Dashboard image uploaded and linked to row', { idx, url: downloadURL });
        } catch (error) {
            logger.error('Failed to upload dashboard image:', error);
            toast.error('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(null);
        }
    };

    const handleSave = async () => {
        if (!canSave) return;
        if (!isDirty) return;
        setIsSaving(true);
        try {
            const updatedBy = (currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'Unknown User'));
            
            // We do not enforce recalculation here anymore, so manual edits can persist.
            let rowsToSave = [...rows];

            // Calculate Net Balance strictly from recalculation
            const finalNetBalance = rowsToSave.reduce((acc, r) => acc + calculateBalance(r), 0);

            const payload = calculatorSavePayload({
                rows: rowsToSave,
                netBalance: finalNetBalance,
                rate: manualMonthlyRate,
                schedule: rateSchedule || [],
                opened: openedRateRef.current,
                invoice: { companyName: invoiceCompanyName, companyAddress: invoiceCompanyAddress, companyTRN: invoiceCompanyTRN, kmLimit: invoiceKMLimit },
                pickupKM,
                comments,
                addons
            });
            let result;
            if (USE_CONTRACTS_V2) {
                // The contracts store: the staff fields only (no comments - a note is saved on its own - and no
                // balance: the server adds the months up), on the version the calculator opened with. Refused
                // (409, nothing written) when someone saved this contract since.
                const view = await saveContractStaff(contractNoOf(contract),
                    staffChangesOf(payload, { openedSchedule: contract._staffSchedule }), versionRef.current, STAFF_SOURCE.calculator);
                versionRef.current = view.version;
                const history = historyAsUpdates(view.history);
                setStoreHistory(history);
                result = { update_history: history, staffVersion: view.version };
            } else {
                // Refused (nothing written) when the contract changed after the calculator opened.
                result = await contractsService.updateContract(contract.id, payload, updatedBy, 'Calc',
                    { expectedLastUpdated: versionRef.current });
                versionRef.current = result.last_updated;
            }
            openedRateRef.current = openedRateState(manualMonthlyRate, rateSchedule || []);
            
            setIsDirty(false);
            setEditingIdx(-1);
            if (onSaveSuccess) {
                // Merge new fields returned from backend (netBalance, update_history)
                const updatedContract = { 
                    ...contract, 
                    monthly_calculations: rowsToSave,
                    netBalance: payload.net_balance,
                    net_balance: payload.net_balance,
                    ...(payload.total_rent !== undefined ? { 'Total Rent': payload.total_rent, total_rent: payload.total_rent } : {}),
                    rate_schedule: rateSchedule || [],
                    invoiceCompanyName, invoiceCompanyAddress, invoiceCompanyTRN, invoiceKMLimit, pickupKM,
                    updateHistory: result?.update_history || contract.updateHistory,
                    comments: comments,
                    addons: addons,
                    updatedBy: `${updatedBy} (Calc)`,
                    ...(USE_CONTRACTS_V2 ? { _staffVersion: result.staffVersion } : { _last_updated: result.last_updated })
                };
                onSaveSuccess([updatedContract]);
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            logger.info('Monthly calculations saved successfully');
        } catch (error) {
            logger.error('Error saving monthly calculations:', error);
            if (error && error.code === CONTRACT_CHANGED) {
                toast.error(error.message);
                // the contracts store: open it again on the latest months (what was typed here was not saved)
                if (USE_CONTRACTS_V2 && onReload) onReload();
            }
            // the contracts store's own sentence: the permission missing (403), the cell refused (400) ...
            else if (USE_CONTRACTS_V2 && error && error.staffError) toast.error(error.message);
            else toast.error('Failed to save changes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const generateRowInvoice = async (idx) => {
        if (!canAccounts) return;
        const row = rows[idx];
        // The number goes through the one invoice-number service (DUP-23). Today (counter off): as before - the
        // typed text, digits get the ALWFQ- prefix. With the counter on, a row without a number is offered the
        // next free one and a new ALWFQ- number is issued (refused when already used); the row's own number
        // prints again without being issued twice.
        const suggested = row.invNo ? '' : (await suggestNextInvoiceNumber()) || '';
        const inputInvNo = await promptDialog({ title: "Enter Invoice Reference Number (INV NO):", label: "INV NO", initialValue: row.invNo || suggested });

        if (inputInvNo === null) return; // User cancelled

        let newInvNo = formatTypedInvoiceNo(inputInvNo);
        const typed = parseInvoiceNumber(newInvNo);
        if (invoiceCounterOn() && typed !== null && parseInvoiceNumber(row.invNo) !== typed) {
            try {
                [newInvNo] = await issueInvoiceNumbers(1, { typed: newInvNo === suggested ? '' : newInvNo, by: currentUser?.email || '' });
            } catch (err) {
                toast.error(err?.message || 'The invoice number could not be issued.');
                return;
            }
        }
        // any other text (e.g. an Ejar invoice number) is printed as typed

        // Update the row in the state
        handleCellChange(idx, 'invNo', newInvNo);

        try {
            const response = await fetch('/Invoice.jpg');
            const imageBuffer = await response.arrayBuffer();
            const imgData = new Uint8Array(imageBuffer);

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const invDate = (row.date && row.date !== '-') ? row.date : formatDateDDMMYYYY(new Date());
            const trnNumber = companySettings.trn;

            // Construct filename: First part of name + RA No + REF
            const customerFirstSegment = (contract.customer || '').split(' ')[0] || 'Customer';
            const raNo = contract.contractNo || 'RA';
            const pdfTitle = `${customerFirstSegment}_${raNo}_${newInvNo}`;

            // Set PDF properties
            doc.setProperties({
                title: pdfTitle,
                subject: 'Tax Invoice',
                author: 'Yelo Unified'
            });

            // Explicitly use the new invNo for the PDF because the state update hasn't rendered yet
            const updatedRow = { ...row, invNo: newInvNo };
            drawRentInvoicePage(doc, updatedRow, contract, invDate, trnNumber, imgData, invoiceCompanyName, invoiceCompanyAddress, invoiceCompanyTRN);

            // Preview in new tab
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');

            logger.info('Row Invoice generated successfully', { invNo: newInvNo });
        } catch (error) {
            logger.error('Failed to generate Row Invoice:', error);
            toast.error('Failed to generate Invoice. Please check the console.');
        }
    };

    const handleGeneratePDF = async () => {
        setIsGeneratingPDF(true);
        try {
            const element = modalRef.current;
            if (!element) return;

            // Capture the element using html2canvas
            const canvas = await html2canvas(element, {
                scale: 2, // Higher quality
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                // This ensures we capture the full content if it's scrollable, 
                // in our case we capture the modal wrapper
                scrollX: 0,
                scrollY: 0,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
                // The on-screen title lives in the dialog header; show the PDF-only title in the capture
                onclone: (clonedDoc) => {
                    const title = clonedDoc.querySelector('[data-pdf-title]');
                    if (title) title.style.display = 'block';
                }
            });

            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [canvas.width, canvas.height]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            
            // Open PDF in a new tab for preview
            const blob = pdf.output('blob');
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            
            logger.info('PDF generated and opened for preview');
        } catch (error) {
            logger.error('PDF Generation failed:', error);
            toast.error('Failed to generate PDF. Please check the console for details.');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const triggerSalikVatCalculation = (idx) => {
        const r = rows[idx];
        if (r && !r.salikHasVat && parseFloat(r.salik) > 0) {
            setSalikVatModalData({ idx, amount: parseFloat(r.salik) });
        }
    };

    // Fill the operational columns per month from the real sources:
    // Payments + Salik/Parking/Other from Ejar (live), Fines from the fines DB.
    // Rent/VAT/dates/invoice/KM stay as they are; the user reviews and then Saves.
    const handleFillFromEjar = async () => {
        const contractNo = contract.contractNo || contract.contract || contract.id;
        if (!contractNo) { toast.error('No contract number on this record.'); return; }
        if (!rows.length) { toast.info('No months to fill yet — open the calculator so the months are generated first.'); return; }
        setIsFilling(true);
        setFillSummary(null);
        try {
            const ejar = await finesService.ejarContract(contractNo);
            // a returned contract Ejar itself closed: what Ejar billed at the close (fuel, extra hours, Shield ...) joins
            // its last month (store_writes.filled_months twin; before Ejar's own close its calculation prices the booking)
            const closed = USE_CONTRACTS_V2
                ? contract.ejar_closed === true
                : (contractStatusOf(contract).toLowerCase() !== 'open'
                    || (contract.type || '').toString().toLowerCase().startsWith('closed'));   // loadData's own test
            const { rows: filled, summary } = fillRowsFromEjar(rows, ejar, { closed });
            setRows(filled);
            setIsDirty(true);
            setFillSummary(summary);
            logger.info('Monthly Calculator filled from Ejar', { contractNo, summary });
        } catch (err) {
            logger.error('Fill from Ejar failed:', err);
            toast.error(`Fill from Ejar failed: ${errorMessage(err)}`);
        } finally {
            setIsFilling(false);
        }
    };

    // Closing (Escape, overlay, X, Cancel) asks first when there are unsaved changes.
    const closingRef = useRef(false);
    const requestClose = async () => {
        if (closingRef.current) return;
        if (!isDirty || !canSave) { onClose(); return; }
        closingRef.current = true;
        try {
            const discard = await confirmDialog({
                title: 'Discard unsaved changes?',
                message: 'This calculator has unsaved changes. Close it without saving?',
                confirmLabel: 'Discard changes',
                cancelLabel: 'Keep editing',
                danger: true,
            });
            if (discard) onClose();
        } finally {
            closingRef.current = false;
        }
    };

    // The History. The contracts store: read again as it opens - a note added, edited or deleted writes its own line
    // there (the months on screen stay as they were opened: only the History is taken from this read).
    const openHistory = async () => {
        setShowHistory(true);
        if (!USE_CONTRACTS_V2) return;
        try {
            setStoreHistory(historyAsUpdates((await loadContractStaff(contractNoOf(contract))).history));
        } catch (err) {
            logger.warn('Could not read the History again - the one read at opening is shown', err);
        }
    };

    const toggleSidebarTab = (tab) => {
        if (showSidebar && activeSidebarTab === tab) setShowSidebar(false);
        else { setShowSidebar(true); setActiveSidebarTab(tab); }
    };

    if (!contract) return null;

    const labelCls = 'text-[11px] uppercase font-semibold tracking-wide text-gray-500';
    const valueCls = 'text-base font-semibold text-gray-900 leading-tight';
    const canEditLeasing = may(PERMISSIONS.edit_monthly_leasing);
    const inlineInputCls = 'text-sm text-gray-900 bg-white border border-gray-300 rounded px-1.5 py-0.5 w-full focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500';
    const inlineValueBtnCls = `flex items-center gap-2 text-left text-sm text-gray-900 ${canEditLeasing ? 'hover:text-purple-700' : 'cursor-default'}`;

    const addonsCount = [
        addons.fullInsurance,
        addons.additionalDriver,
        parseFloat(addons.extraKM) > 0
    ].filter(Boolean).length;
    const totalFinesCount = rows.reduce((sum, r) => sum + (r.fines_list?.length || 0), 0);
    const totalParkingCount = rows.reduce((sum, r) => sum + (r.parking_list?.length || 0), 0);
    const totalOthersCount = rows.reduce((sum, r) => sum + (r.other_list?.length || 0), 0);
    const ratesCount = (rateSchedule || []).length;
    // Ejar's own comments (read-only), and the ones written inside each month - shown on the month (owner, 4 Oct 2026)
    const ejarComments = ejarCommentsOf(contract);
    const monthNotes = commentsByMonth(rows, ejarComments);
    const commentsCount = (comments || []).length + ejarComments.length;

    // Pick-up / Drop-off columns (owner, 27 Sep 2026): the first row starts at the contract's pick-up and a
    // returned contract's last row ends at its drop-off - those two show the contract's own date + time.
    // Every other row is a billing date with no time on it, so it stays "dd/mm/yyyy". Display only.
    const pickupText = formatContractDateTime(contract.pickupDate || contract['Pick-up Date']);
    const dropoffText = formatContractDateTime(contract.dropoffDate || contract['Drop-off Date']);
    const sameDay = (a, b) => {
        const x = parseSheetDate(String(a || ''));
        const y = parseSheetDate(String(b || ''));
        return !!(x && y && x.getTime() === y.getTime());
    };
    const rowStartText = (row, idx) => (idx === 0 && pickupText && sameDay(row.start, pickupText.slice(0, 10)) ? pickupText : row.start);
    const rowEndText = (row, idx) => (idx === rows.length - 1 && dropoffText && sameDay(row.end, dropoffText.slice(0, 10)) ? dropoffText : row.end);

    return (
        <Dialog
            open
            onClose={requestClose}
            title="Monthly Calculator"
            description={[contract.contractNo, contract.customer].filter(Boolean).join(' · ') || undefined}
            size="full"
            className="!max-w-[98vw]"
            bodyClassName="!p-0 flex flex-col"
        >
            <div
                className="relative flex flex-col flex-1 min-h-0 bg-white"
                ref={modalRef}
            >
                {/* Loading Overlay */}
                {isLoadingData && (
                    <div className="absolute inset-0 bg-white z-[100] flex items-center justify-center">
                        <LoadingSpinner message="Loading Contract Data..." />
                    </div>
                )}
                {!isLoadingData && loadError && (
                    <div role="alert" className="flex-none px-4 py-2 text-center text-sm font-semibold text-red-700 bg-red-50 border-b border-red-200">
                        This contract could not be loaded completely: {loadError}. Close and open it again before saving.
                    </div>
                )}

                {/* Title shown only in the "Download as PDF" snapshot */}
                <h2 data-pdf-title style={{ display: 'none' }} className="px-4 pt-4 text-lg font-semibold text-gray-900">Monthly Calculator</h2>

                {/* Header - Fixed at top */}
                <div className="flex-none p-4 border-b border-gray-200 bg-white z-10 overflow-y-auto max-h-[50vh] xl:max-h-none">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                            <Card><div className="flex flex-col xl:flex-row xl:items-center gap-4">
                                {/* LEFT COLUMN: Data Grid */}
                                <div className="flex-1 flex flex-col gap-4">
                                    {/* Row 1: Basic Info */}
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                                        <button
                                            type="button"
                                            className="flex flex-col items-start text-left min-w-[160px]"
                                            title="Copy contract number"
                                            onClick={() => {
                                                navigator.clipboard.writeText(contract.contractNo || '');
                                                setCopyFeedback(true);
                                                setTimeout(() => setCopyFeedback(false), 2000);
                                            }}
                                        >
                                            <span className={`${labelCls} mb-1`}>
                                                Contract No.
                                            </span>
                                            <span className={`text-lg font-semibold font-mono leading-none ${copyFeedback ? 'text-green-700' : 'text-purple-700'}`} aria-live="polite">
                                                {copyFeedback ? 'Copied!' : contract.contractNo || 'N/A'}
                                            </span>
                                        </button>

                                        <div className="flex flex-col min-w-[180px] border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Customer Name</span>
                                            <span className={valueCls}>{contract.customer || 'N/A'}</span>
                                        </div>

                                        <div className="flex flex-col min-w-[150px] border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Phone Number</span>
                                            <div className="mt-1">
                                                {hasPermission('contracts_send_whatsapp') ? (
                                                    <RowActions
                                                        row={contract}
                                                        switchBackOf={switchBackOf}
                                                        onCopy={onCopy}
                                                    />
                                                ) : <span className="ds-cell-phone">{formatPhone(phoneDigits(contract.phoneNumber)) || '—'}</span>}
                                            </div>
                                        </div>

                                        <div className="flex flex-col min-w-[120px] border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Plate Number</span>
                                            <span className={`${valueCls} font-mono`}>{contract.invygoPlate || contract.ejarPlate || contract.plateNo || 'N/A'}</span>
                                        </div>
                                        <div className="flex flex-col min-w-[150px] border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Vehicle</span>
                                            <span className={`${valueCls} truncate`} title={contract.invygoModel || contract.ejarModel || contract.make || contract.model || 'N/A'}>
                                                {contract.invygoModel || contract.ejarModel || contract.make || contract.model || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex flex-col min-w-[140px] border-l border-gray-200 pl-6">
                                            <label htmlFor="mc-monthly-rate" className={labelCls}>Monthly Rate</label>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <input
                                                    id="mc-monthly-rate"
                                                    disabled={!canEditLeasing}
                                                    type="number"
                                                    value={manualMonthlyRate === 0 ? '' : manualMonthlyRate}
                                                    onChange={(e) => {
                                                        const valStr = e.target.value;
                                                        setManualMonthlyRate(valStr === '' ? '' : (parseFloat(valStr) || 0));
                                                        setIsDirty(true);
                                                    }}
                                                    onBlur={() => {
                                                        let finalRate = manualMonthlyRate;
                                                        if (!finalRate || finalRate <= 0) {
                                                            finalRate = monthlyRateOfContract(contract);
                                                            setManualMonthlyRate(finalRate);
                                                        }

                                                        if (finalRate > 0 && rows && rows.length > 0) {
                                                            const nextSchedule = scheduleWithCurrentRate(finalRate);
                                                            if (nextSchedule !== rateSchedule) setRateSchedule(nextSchedule);
                                                            const newRows = rows.map((r, i) => {
                                                                const newTotalRent = getProportionalRent(r, i, finalRate, rows, undefined, nextSchedule);
                                                                if (newTotalRent === 0) return r;
                                                                const newRentRaw = parseFloat(withoutVat(newTotalRent).toFixed(2));
                                                                const newVatRaw = parseFloat((newTotalRent - newRentRaw).toFixed(2));
                                                                return {
                                                                    ...r,
                                                                    totalRent: newTotalRent,
                                                                    rent: newRentRaw,
                                                                    vat: newVatRaw
                                                                };
                                                            });
                                                            setRows(newRows);
                                                        }
                                                    }}
                                                    className="w-24 bg-white border border-gray-300 rounded px-2 py-0.5 text-base font-semibold text-gray-900 tabular-nums focus:ring-2 focus:ring-purple-200 focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                />
                                                <span className="text-[11px] text-gray-500">AED</span>
                                            </div>
                                            <span className="text-[11px] text-gray-500 mt-0.5">* Edit to fix calculations</span>
                                            {contract.ejar_opening_comment && (
                                                <span
                                                    className="text-[11px] text-gray-700 mt-1 max-w-[260px] truncate cursor-help"
                                                    title={`Ejar opening comment (source of the rate: ${contract.rent_source || 'ejar'})\n\n${contract.ejar_opening_comment}${contract.rent_conflict ? `\n\n⚠ ${contract.rent_conflict}` : ''}`}
                                                >
                                                    <span className="font-semibold text-gray-600">Ejar:</span> {String(contract.ejar_opening_comment).split('\n').filter(Boolean).join(' · ')}
                                                    {contract.rent_conflict ? <Icon glyph={ICONS.alert} size="xs" tone="warning" label="Rent conflict" className="ml-1 align-middle" /> : null}
                                                </span>
                                            )}
                                        </div>


                                        <div className="flex flex-col min-w-[150px] border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Net Balance</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`text-lg font-semibold tabular-nums ${rows.reduce((acc, r) => acc + calculateBalance(r), 0) > 0 ? 'text-red-600' : 'text-green-700'}`}>
                                                    {rows.reduce((acc, r) => acc + calculateBalance(r), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-xs">AED</span>
                                                </span>
                                                <IconButton
                                                    icon={ICONS.history}
                                                    label="View update history"
                                                    size="sm"
                                                    onClick={openHistory}
                                                    data-html2canvas-ignore
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="w-full border-t border-gray-200"></div>

                                    {/* Row 2: Calculations & Settings */}
                                    <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                                        {isLeasingCustomer && (
                                            <>
                                                <div className="flex flex-col min-w-[200px] xl:flex-1">
                                                    <span className={labelCls}>Invoice Company</span>
                                                    <div className="flex items-center gap-2 group mt-0.5">
                                                        {editingIdx === -98 && canEditLeasing ? (
                                                            <input
                                                                type="text"
                                                                aria-label="Invoice company"
                                                                value={invoiceCompanyName}
                                                                onChange={(e) => { setInvoiceCompanyName(e.target.value); setIsDirty(true); }}
                                                                onBlur={() => setEditingIdx(-1)}
                                                                autoFocus
                                                                className={inlineInputCls}
                                                            />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={inlineValueBtnCls}
                                                                disabled={!canEditLeasing}
                                                                onClick={() => canEditLeasing && setEditingIdx(-98)}
                                                            >
                                                                <span>{invoiceCompanyName || 'Click to add Company'}</span>
                                                                {canEditLeasing && <Icon glyph={ICONS.edit} size="xs" className="opacity-0 group-hover:opacity-100" />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col min-w-[200px] xl:flex-1 xl:border-l border-gray-200 xl:pl-6">
                                                    <span className={labelCls}>Invoice Address</span>
                                                    <div className="flex items-center gap-2 group mt-0.5">
                                                        {editingIdx === -97 && canEditLeasing ? (
                                                            <input
                                                                type="text"
                                                                aria-label="Invoice address"
                                                                value={invoiceCompanyAddress}
                                                                onChange={(e) => { setInvoiceCompanyAddress(e.target.value); setIsDirty(true); }}
                                                                onBlur={() => setEditingIdx(-1)}
                                                                autoFocus
                                                                className={inlineInputCls}
                                                            />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={inlineValueBtnCls}
                                                                disabled={!canEditLeasing}
                                                                onClick={() => canEditLeasing && setEditingIdx(-97)}
                                                            >
                                                                <span>{invoiceCompanyAddress || 'Click to add Address'}</span>
                                                                {canEditLeasing && <Icon glyph={ICONS.edit} size="xs" className="opacity-0 group-hover:opacity-100" />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col min-w-[150px] xl:flex-1 xl:border-l border-gray-200 xl:pl-6">
                                                    <span className={labelCls}>Invoice TRN</span>
                                                    <div className="flex items-center gap-2 group mt-0.5">
                                                        {editingIdx === -96 && canEditLeasing ? (
                                                            <input
                                                                type="text"
                                                                aria-label="Invoice TRN"
                                                                value={invoiceCompanyTRN}
                                                                onChange={(e) => { setInvoiceCompanyTRN(e.target.value); setIsDirty(true); }}
                                                                onBlur={() => setEditingIdx(-1)}
                                                                autoFocus
                                                                className={inlineInputCls}
                                                            />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={inlineValueBtnCls}
                                                                disabled={!canEditLeasing}
                                                                onClick={() => canEditLeasing && setEditingIdx(-96)}
                                                            >
                                                                <span>{invoiceCompanyTRN || 'Click to add TRN'}</span>
                                                                {canEditLeasing && <Icon glyph={ICONS.edit} size="xs" className="opacity-0 group-hover:opacity-100" />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className={`flex flex-col min-w-[120px] xl:flex-1 ${isLeasingCustomer ? 'xl:border-l border-gray-200 xl:pl-6' : ''}`}>
                                            <label htmlFor="mc-km-limit" className={labelCls}>KM LIMIT / MON</label>
                                            <input
                                                id="mc-km-limit"
                                                type="number"
                                                value={invoiceKMLimit}
                                                onChange={(e) => { setInvoiceKMLimit(parseFloat(e.target.value) || 0); setIsDirty(true); }}
                                                className={`text-base font-semibold text-gray-900 tabular-nums bg-transparent border-b border-gray-300 outline-none w-24 focus:border-purple-500 mt-0.5 ${!canEditLeasing ? 'opacity-70 pointer-events-none border-transparent' : ''}`}
                                                disabled={!canEditLeasing}
                                            />
                                        </div>

                                        <div className="flex flex-col min-w-[150px] xl:flex-1 xl:border-l border-gray-200 xl:pl-6">
                                            <span className={labelCls}>Total Usage</span>
                                            <span className="text-base font-semibold text-gray-900 tabular-nums mt-1">
                                                {(() => {
                                                    const kmRows = rows.filter(r => (parseFloat(r.currentKM) || 0) > 0);
                                                    if (kmRows.length === 0) return '0';
                                                    const lastRow = kmRows[kmRows.length - 1];
                                                    const totalUsage = (parseFloat(lastRow.currentKM) || pickupKM) - pickupKM;
                                                    return totalUsage.toLocaleString();
                                                })()} <span className="text-[11px] text-gray-500">KM</span>
                                            </span>
                                        </div>

                                        <div className="flex flex-col min-w-[120px] xl:flex-1 border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Status</span>
                                            {(() => {
                                                const kmRows = rows.filter(r => (parseFloat(r.currentKM) || 0) > 0);
                                                if (kmRows.length === 0) return <span className="text-sm text-gray-900">-</span>;
                                                const lastRow = kmRows[kmRows.length - 1];
                                                const totalUsage = (parseFloat(lastRow.currentKM) || pickupKM) - pickupKM;
                                                const allowed = invoiceKMLimit * kmRows.length;
                                                const diff = totalUsage - allowed;
                                                const isOver = diff > 0;
                                                return (
                                                    <span className={`text-sm font-semibold tabular-nums mt-1 ${isOver ? 'text-red-700' : 'text-green-700'}`}>
                                                        {isOver ? '+' : ''}{diff.toLocaleString()} KM
                                                    </span>
                                                );
                                            })()}
                                        </div>

                                        <div className="flex flex-col min-w-[120px] xl:flex-1 border-l border-gray-200 pl-6">
                                            <span className={labelCls}>Last Update</span>
                                            <span className="text-sm text-gray-900 tabular-nums mt-1">
                                                {(() => {
                                                    const kmRows = rows.filter(r => (parseFloat(r.currentKM) || 0) > 0);
                                                    if (kmRows.length === 0) return '-';
                                                    return kmRows[kmRows.length - 1].end;
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div></Card>
                        </div>

                        {/* Side panel toggles */}
                        <div className="flex flex-wrap items-center gap-2 mb-auto xl:ml-4" data-html2canvas-ignore>
                            <SidebarTrigger icon={addonsCount > 0 ? ICONS.star : ICONS.plus} label="Addons" count={addonsCount} active={addonsCount > 0} selected={showSidebar && activeSidebarTab === 'addons'} onClick={() => toggleSidebarTab('addons')} />
                            <SidebarTrigger icon={ICONS.fines} label="Fines" count={totalFinesCount} active={totalFinesCount > 0} selected={showSidebar && activeSidebarTab === 'fines'} onClick={() => toggleSidebarTab('fines')} />
                            <SidebarTrigger icon={ICONS.car} label="Parking" count={totalParkingCount} active={totalParkingCount > 0} selected={showSidebar && activeSidebarTab === 'parking'} onClick={() => toggleSidebarTab('parking')} />
                            <SidebarTrigger icon={ICONS.list} label="Other" count={totalOthersCount} active={totalOthersCount > 0} selected={showSidebar && activeSidebarTab === 'others'} onClick={() => toggleSidebarTab('others')} />
                            <SidebarTrigger icon={ICONS.chart} label="Rates" count={ratesCount} active={ratesCount > 1} selected={showSidebar && activeSidebarTab === 'rates'} onClick={() => toggleSidebarTab('rates')} />
                            <SidebarTrigger icon={ICONS.edit} label="Comments" count={commentsCount || 0} active={commentsCount > 0} selected={showSidebar && activeSidebarTab === 'comments'} onClick={() => toggleSidebarTab('comments')} />
                        </div>
                    </div>
                </div>

                {/* Sidebar - side panel */}
                {showSidebar && (
                    <div
                        ref={sidebarRef}
                        role="region"
                        aria-label="Details panel"
                        className="absolute right-4 top-4 bottom-[100px] w-[calc(100%-2rem)] sm:w-[380px] bg-white border border-gray-200 z-[200] shadow-md flex flex-col rounded-lg overflow-hidden"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                {activeSidebarTab === 'addons' ? (
                                    <><Icon glyph={ICONS.star} size="xs" tone="muted" /> Addons Editor</>
                                ) : activeSidebarTab === 'fines' ? (
                                    <><Icon glyph={ICONS.fines} size="xs" tone="muted" /> Fines Register</>
                                ) : activeSidebarTab === 'parking' ? (
                                    <><Icon glyph={ICONS.car} size="xs" tone="muted" /> Parking Charges</>
                                ) : activeSidebarTab === 'others' ? (
                                    <><Icon glyph={ICONS.list} size="xs" tone="muted" /> Other Charges</>
                                ) : activeSidebarTab === 'rates' ? (
                                    <><Icon glyph={ICONS.chart} size="xs" tone="muted" /> Rate changes</>
                                ) : (
                                    <><Icon glyph={ICONS.edit} size="xs" tone="muted" /> Comments</>
                                )}
                            </h3>
                            <IconButton
                                icon={ICONS.close}
                                label="Close panel"
                                size="sm"
                                onClick={() => setShowSidebar(false)}
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            {activeSidebarTab === 'addons' ? (
                                <AddonsView
                                    readOnly={!canEditLeasing}
                                    addons={addons}
                                    setAddons={setAddons}
                                    setIsDirty={setIsDirty}
                                />
                            ) : activeSidebarTab === 'fines' ? (
                                <FinesView rows={rows} />
                            ) : activeSidebarTab === 'parking' ? (
                                <ParkingView rows={rows} />
                            ) : activeSidebarTab === 'others' ? (
                                <OtherChargesView rows={rows} />
                            ) : activeSidebarTab === 'rates' ? (
                                <RateScheduleView readOnly={!canEditLeasing} schedule={rateSchedule || []} setSchedule={setRateSchedule} rows={rows} setRows={setRows}
                                    setIsDirty={setIsDirty} baseRate={manualMonthlyRate} />
                            ) : (
                                <CommentsView
                                    comments={comments}
                                    ejarComments={ejarComments}
                                    newCommentText={newCommentText}
                                    setNewCommentText={setNewCommentText}
                                    handleAddComment={handleAddComment}
                                    handleEditComment={handleEditComment}
                                    handleDeleteComment={handleDeleteComment}
                                    hasPermission={may}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Scrollable Middle Section */}
                <div className="flex-1 overflow-hidden p-4 bg-gray-50 min-h-0">
                    {/* Table Container */}
                    <TableContainer className="h-full flex flex-col">
                        <div className="flex-1 overflow-auto min-h-0">
                            <Table sticky className="min-w-[1000px]">
                                <TableHead className="sticky top-0 z-40">
                                    <TableRow>
                                        <TableHeader data-html2canvas-ignore>Edit</TableHeader>
                                        <TableHeader>Pick-up Date</TableHeader>
                                        <TableHeader>Drop-off Date</TableHeader>
                                        <TableHeader>Rent</TableHeader>
                                        <TableHeader>VAT</TableHeader>
                                        <TableHeader>Total Rent</TableHeader>
                                        {showProportional && <TableHeader>Usage Rent (Today)</TableHeader>}
                                        <TableHeader>SALIK</TableHeader>
                                        <TableHeader>Salik Period</TableHeader>
                                        <TableHeader>Parking</TableHeader>
                                        <TableHeader>T FINE</TableHeader>
                                        <TableHeader>Other</TableHeader>
                                        <TableHeader>TOTAL</TableHeader>
                                        <TableHeader>PAID</TableHeader>
                                        <TableHeader>DATE</TableHeader>
                                        <TableHeader>Balance</TableHeader>
                                        <TableHeader>INV NO</TableHeader>
                                        <TableHeader>Start KM</TableHeader>
                                        <TableHeader>End KM</TableHeader>
                                        <TableHeader data-html2canvas-ignore>Dash Photo</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx} className={editingIdx === idx ? 'bg-purple-50 relative z-[60]' : ''}
                                            title={monthNotes[idx]?.length ? commentsTooltip(monthNotes[idx]) : undefined}>
                                            <TableCell data-html2canvas-ignore>
                                                <IconButton
                                                    icon={editingIdx === idx ? ICONS.check : ICONS.edit}
                                                    label={editingIdx === idx ? `Finish editing ${row.start}` : `Edit row ${row.start}`}
                                                    title={editingIdx === idx ? "Finish Editing" : "Edit Row"}
                                                    size="xs"
                                                    variant={editingIdx === idx ? 'primary' : 'ghost'}
                                                    onClick={() => setEditingIdx(editingIdx === idx ? -1 : idx)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <span className="ds-cell-date">{rowStartText(row, idx)}</span>
                                                {monthNotes[idx]?.length > 0 && (
                                                    <span className="ms-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-purple-700 align-middle"
                                                        role="img" aria-label={`${monthNotes[idx].length} Ejar comment(s) in this month`}>
                                                        <Icon glyph={ICONS.document} size="xs" />{monthNotes[idx].length}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="relative">
                                                <span className="ds-cell-date">{rowEndText(row, idx)}</span>
                                                {row.isEarlyReturn && <CellMuted>Early Return</CellMuted>}
                                            </TableCell>
                                            <TableCell>
                                                {row.rent > 0 ? <CellMoney value={row.rent} /> : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.vat > 0 ? <CellMoney value={row.vat} /> : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <EditableCell
                                                    isEditing={editingIdx === idx}
                                                    value={row.totalRent || 0}
                                                    onChange={(val) => handleCellChange(idx, 'totalRent', val)}
                                                    disabled={!may(PERMISSIONS.edit_monthly_leasing)}
                                                />
                                            </TableCell>
                                            {showProportional && (
                                                <TableCell>
                                                    <CellMoney value={getProportionalRent(row, idx, undefined, rows, simulationDate)} />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex flex-col items-center">
                                                    <EditableCell
                                                        isEditing={editingIdx === idx}
                                                        value={row.salik}
                                                        onChange={(val) => handleCellChange(idx, 'salik', val)}
                                                        onBlur={() => triggerSalikVatCalculation(idx)}
                                                        disabled={!may(PERMISSIONS.edit_monthly_salik)}
                                                    />
                                                    {row.salikHasVat && <CellMuted>Inc VAT</CellMuted>}
                                                </div>
                                            </TableCell>
                                            <TableCell className="min-w-[160px]">
                                                <div className="flex items-center gap-1 justify-center">
                                                    <EditableCell
                                                        isEditing={editingIdx === idx}
                                                        value={row.salikFrom || ''}
                                                        onChange={(val) => handleCellChange(idx, 'salikFrom', val)}
                                                        type="text"
                                                        className="text-[11px] w-17"
                                                        displayClass="ds-cell-date"
                                                        disabled={!may(PERMISSIONS.edit_monthly_salik)}
                                                    />
                                                    <span className="text-gray-500">-</span>
                                                    <EditableCell
                                                        isEditing={editingIdx === idx}
                                                        value={row.salikTo || ''}
                                                        onChange={(val) => handleCellChange(idx, 'salikTo', val)}
                                                        type="text"
                                                        className="text-[11px] w-17"
                                                        displayClass="ds-cell-date"
                                                        disabled={!may(PERMISSIONS.edit_monthly_salik)}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="group relative">
                                                <div className="flex items-center justify-center gap-1">
                                                    <EditableCell
                                                        isEditing={false}
                                                        value={row.parking}
                                                        onChange={(val) => handleCellChange(idx, 'parking', val)}
                                                        className="text-center"
                                                        disabled={true}
                                                    />
                                                    <IconButton
                                                        icon={ICONS.history}
                                                        label={`Parking charges for ${row.start}`}
                                                        title="Browse Month Parking Charges"
                                                        size="xs"
                                                        variant={row.parking_list?.length > 0 ? 'secondary' : 'ghost'}
                                                        className={row.parking_list?.length > 0 ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
                                                        onClick={() => {
                                                            setCurrentParkingRowIndex(idx);
                                                            setShowParkingModal(true);
                                                        }}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="group relative">
                                                <div className="flex items-center justify-center gap-1">
                                                    <EditableCell
                                                        isEditing={false}
                                                        value={row.fine}
                                                        onChange={(val) => handleCellChange(idx, 'fine', val)}
                                                        className="text-center"
                                                        displayClass={`ds-cell-num ${toneClass(row.fine > 0 ? 'danger' : undefined)}`}
                                                        disabled={true}
                                                    />
                                                    <IconButton
                                                        icon={ICONS.history}
                                                        label={`Fines for ${row.start}`}
                                                        title="Browse Month Fines"
                                                        size="xs"
                                                        variant={row.fines_list?.length > 0 ? 'secondary' : 'ghost'}
                                                        className={row.fines_list?.length > 0 ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
                                                        onClick={() => {
                                                            setCurrentFineRowIndex(idx);
                                                            setShowFinesModal(true);
                                                        }}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="group relative">
                                                <div className="flex items-center justify-center gap-1">
                                                    <EditableCell
                                                        isEditing={false}
                                                        value={row.other}
                                                        onChange={(val) => handleCellChange(idx, 'other', val)}
                                                        className="text-center"
                                                        disabled={true}
                                                    />
                                                    <IconButton
                                                        icon={ICONS.history}
                                                        label={`Other charges for ${row.start}`}
                                                        title="Browse Month Other Charges"
                                                        size="xs"
                                                        variant={row.other_list?.length > 0 ? 'secondary' : 'ghost'}
                                                        className={row.other_list?.length > 0 ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
                                                        onClick={() => {
                                                            setCurrentOtherRowIndex(idx);
                                                            setShowOtherChargesModal(true);
                                                        }}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <CellMoney value={calculateTotal(row)} />
                                            </TableCell>
                                            <TableCell>
                                                <EditableCell isEditing={editingIdx === idx} value={row.paid} onChange={(val) => handleCellChange(idx, 'paid', val)} displayClass={`ds-cell-num ${toneClass(row.paid > 0 ? 'success' : undefined)}`} disabled={!may(PERMISSIONS.edit_monthly_accounts)} />
                                            </TableCell>
                                            <TableCell>
                                                <EditableCell isEditing={editingIdx === idx} value={row.date} onChange={(val) => handleCellChange(idx, 'date', val)} type="text" displayClass="ds-cell-date" disabled={!may(PERMISSIONS.edit_monthly_accounts)} />
                                            </TableCell>
                                            <TableCell>
                                                {/* Owed (red) / credit (green): the balance status colour stays. */}
                                                <span className="ds-cell-num" style={{ color: calculateBalance(row) > 0 ? 'var(--danger)' : 'var(--success)' }}>
                                                    {calculateBalance(row).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                </span>
                                            </TableCell>
                                            <TableCell className="group">
                                                <div className="flex items-center gap-1 justify-center">
                                                    <EditableCell isEditing={editingIdx === idx} value={row.invNo} onChange={(val) => handleCellChange(idx, 'invNo', val)} type="text" displayClass="ds-cell-id" disabled={!may(PERMISSIONS.edit_monthly_accounts)} />
                                                    {editingIdx !== idx && canAccounts && (
                                                        <IconButton
                                                            icon={ICONS.print}
                                                            label={`Print tax invoice for ${row.start}`}
                                                            title="Print Tax Invoice"
                                                            size="xs"
                                                            className="opacity-0 group-hover:opacity-100 focus:opacity-100"
                                                            onClick={() => generateRowInvoice(idx)}
                                                            data-html2canvas-ignore
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const startKMVal = idx === 0 ? pickupKM : (parseFloat(rows[idx - 1].currentKM) || pickupKM);
                                                    return <span className="ds-cell-num">{startKMVal.toLocaleString()}</span>;
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <EditableCell isEditing={editingIdx === idx} value={row.currentKM || 0} onChange={(val) => handleCellChange(idx, 'currentKM', val)} disabled={!may(PERMISSIONS.edit_monthly_ops)} />
                                            </TableCell>
                                            <TableCell data-html2canvas-ignore>
                                                <div className="flex items-center justify-center gap-2">
                                                    {row.dashboardImage ? (
                                                        <a
                                                            href={row.dashboardImage}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100"
                                                            title="View Dashboard Photo"
                                                            aria-label={`View dashboard photo for ${row.start}`}
                                                        >
                                                            <Icon glyph={ICONS.visible || ICONS.edit} size="xs" />
                                                        </a>
                                                    ) : null}

                                                    {canOps && <div className="relative">
                                                        <input
                                                            type="file"
                                                            id={`upload-${idx}`}
                                                            className="sr-only"
                                                            accept="image/*"
                                                            onChange={(e) => handleFileUpload(idx, e.target.files[0])}
                                                        />
                                                        <label
                                                            htmlFor={`upload-${idx}`}
                                                            className={`p-1.5 rounded-md border cursor-pointer flex items-center justify-center ${isUploading === idx ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-white text-purple-700 border-gray-200 hover:bg-purple-50'}`}
                                                            title="Upload Dashboard Photo"
                                                        >
                                                            {isUploading === idx ? (
                                                                <LoadingSpinner size="sm" />
                                                            ) : (
                                                                <Icon glyph={ICONS.attach || ICONS.edit} size="xs" label="Upload dashboard photo" />
                                                            )}
                                                        </label>
                                                    </div>}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TableContainer>
                </div>

                {/* Footer - Fixed at bottom */}
                <div className="flex-none py-2 px-3 sm:px-4 bg-white border-t border-gray-200 flex flex-wrap items-center justify-between z-10 gap-x-3 gap-y-2" data-html2canvas-ignore>

                    {/* Left: Controls */}
                    <div className="flex items-center gap-2 overflow-x-auto shrink-0">
                        {may(PERMISSIONS.edit_monthly_accounts) && (
                            <Button
                                size="sm"
                                onClick={handleFillFromEjar}
                                isLoading={isFilling}
                                disabled={isLoadingData}
                                title="Fill Payments + Salik/Parking/Other from Ejar and Fines from the database, month by month"
                                leadingIcon={<Icon glyph={ICONS.download} size="xs" />}
                                className="shrink-0"
                            >
                                {isFilling ? 'Filling…' : 'Fill from Ejar'}
                            </Button>
                        )}
                        {fillSummary && !isFilling && (
                            <span className="flex items-center gap-2 text-xs font-medium bg-green-50 text-green-800 border border-green-200 px-2.5 h-8 rounded-md shrink-0 whitespace-nowrap tabular-nums" role="status">
                                <Icon glyph={ICONS.check} size="xs" />
                                {fillSummary.paymentsCount}P · {fillSummary.finesCount}F · {fillSummary.otherCount}O
                                <button type="button" onClick={() => setFillSummary(null)} className="ml-1 text-green-700 hover:text-green-900" aria-label="Dismiss fill summary"><Icon glyph={ICONS.x} size="xs" /></button>
                            </span>
                        )}
                        <Button
                            size="sm"
                            variant={showProportional ? 'primary' : 'secondary'}
                            onClick={() => setShowProportional(!showProportional)}
                            aria-pressed={showProportional}
                            leadingIcon={<Icon glyph={ICONS.clock} size="xs" />}
                            className="shrink-0"
                        >
                            {showProportional ? 'Hide Today' : 'Today Usage'}
                        </Button>
                        {showProportional && (
                            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-md px-1.5 h-8 shrink-0" role="group" aria-label="Simulation date">
                                <IconButton
                                    icon={ICONS.minus || ICONS.chevronLeft}
                                    label="Previous day"
                                    title="Decrease Day"
                                    size="xs"
                                    onClick={() => adjustSimulationDate(-1)}
                                />
                                <div className="flex flex-col items-center px-1">
                                    <span className="text-[11px] text-gray-500 leading-none">Simulation</span>
                                    <span className="text-xs text-gray-900 font-semibold tabular-nums leading-tight">
                                        {formatDateDDMMYYYY(simulationDate)}
                                    </span>
                                </div>
                                <IconButton
                                    icon={ICONS.plus || ICONS.chevronRight}
                                    label="Next day"
                                    title="Increase Day"
                                    size="xs"
                                    onClick={() => adjustSimulationDate(1)}
                                />
                            </div>
                        )}
                        {isDirty && !showProportional && (
                            <span className="text-xs text-amber-800 font-medium flex items-center gap-1 bg-amber-50 px-2.5 h-8 rounded-md shrink-0 border border-amber-200" role="status">
                                <Icon glyph={ICONS.alert} size="xs" /> Unsaved
                            </span>
                        )}
                    </div>

                    {/* Center: Compact Summary */}
                    <Card padding="none" className="flex-1 min-w-[280px] mx-auto"><div className="flex items-center justify-center gap-3 sm:gap-5 px-4 py-1.5 overflow-x-auto">
                        <div className="flex flex-col items-start pr-3 sm:pr-4 border-r border-gray-200 shrink-0">
                            <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide leading-none mb-1">{showProportional ? 'Simulated' : 'Total'}</span>
                            <span className="text-sm font-semibold text-gray-900 leading-none">Summary</span>
                        </div>
                        <div className="flex gap-4 sm:gap-8 shrink-0 tabular-nums">
                            <div className="flex flex-col items-end">
                                <span className={`text-[11px] uppercase font-semibold tracking-wide mb-0.5 ${showProportional ? 'text-amber-800' : 'text-gray-500'}`}>
                                    {showProportional ? 'Total Used' : 'Total'}
                                </span>
                                <span className={`text-base sm:text-lg font-semibold leading-none ${showProportional ? 'text-amber-800' : 'text-gray-900'}`}>
                                    {rows.reduce((sum, r, idx) => {
                                        const baseTotal = (parseFloat(r.salik) || 0) + (parseFloat(r.parking) || 0) + (parseFloat(r.fine) || 0) + (parseFloat(r.other) || 0);
                                        const rentToUse = showProportional ? getProportionalRent(r, idx, undefined, rows, simulationDate) : (parseFloat(r.totalRent) || 0);
                                        return sum + baseTotal + rentToUse;
                                    }, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-[11px] text-gray-500 uppercase font-semibold tracking-wide mb-0.5">Paid</span>
                                <span className="text-base sm:text-lg font-semibold text-green-700 leading-none">
                                    {rows.reduce((sum, r) => sum + (parseFloat(r.paid) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className={`text-[11px] uppercase font-semibold tracking-wide mb-0.5 ${showProportional ? 'text-amber-800' : 'text-gray-500'}`}>
                                    {showProportional ? 'Balance' : 'Net'}
                                </span>
                                <span className={`text-base sm:text-lg font-semibold leading-none ${rows.reduce((sum, r, idx) => {
                                        const baseTotal = (parseFloat(r.salik) || 0) + (parseFloat(r.parking) || 0) + (parseFloat(r.fine) || 0) + (parseFloat(r.other) || 0);
                                        const rentToUse = showProportional ? getProportionalRent(r, idx, undefined, rows, simulationDate) : (parseFloat(r.totalRent) || 0);
                                        const total = baseTotal + rentToUse;
                                        return sum + (total - (parseFloat(r.paid) || 0));
                                    }, 0) > 0 ? (showProportional ? 'text-amber-800' : 'text-red-600') : 'text-green-700'}`}>
                                    {rows.reduce((sum, r, idx) => {
                                        const baseTotal = (parseFloat(r.salik) || 0) + (parseFloat(r.parking) || 0) + (parseFloat(r.fine) || 0) + (parseFloat(r.other) || 0);
                                        const rentToUse = showProportional ? getProportionalRent(r, idx, undefined, rows, simulationDate) : (parseFloat(r.totalRent) || 0);
                                        const total = baseTotal + rentToUse;
                                        return sum + (total - (parseFloat(r.paid) || 0));
                                    }, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>
                    </div></Card>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 shrink-0 justify-end">
                        <Button size="sm" onClick={requestClose}>
                            Cancel
                        </Button>
                        {canSave && <Button
                            size="sm"
                            variant="primary"
                            className="min-w-[90px]"
                            onClick={handleSave}
                            disabled={(!isDirty && !saveSuccess) || isSaving}
                            isLoading={isSaving}
                            leadingIcon={saveSuccess && !isSaving ? <Icon glyph={ICONS.check} size="sm" /> : undefined}
                        >
                            {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save'}
                        </Button>}
                        {hasPermission('export_monthly_calculator') && <Button
                            size="sm"
                            iconOnly
                            aria-label="Download as PDF"
                            title="Download as PDF"
                            onClick={handleGeneratePDF}
                            isLoading={isGeneratingPDF}
                            leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                        />}
                    </div>
                </div>

                {showHistory && (
                    <HistoryModal
                        history={USE_CONTRACTS_V2 ? storeHistory : contract.updateHistory}
                        onClose={() => setShowHistory(false)}
                    />
                )}

                {showFinesModal && (
                    <FineDetailModal
                        readOnly={!canAccounts}
                        monthName={rows[currentFineRowIndex]?.start}
                        fines={rows[currentFineRowIndex]?.fines_list || []}
                        allExistingTickets={rows.reduce((acc, r, i) => {
                            if (i !== currentFineRowIndex && r.fines_list) {
                                r.fines_list.forEach(f => acc.push(String(f.ticketNo).trim().toLowerCase()));
                            }
                            return acc;
                        }, [])}
                        onSave={(newFines) => {
                            const totalAmount = newFines.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);
                            handleCellChange(currentFineRowIndex, {
                                fines_list: newFines,
                                fine: totalAmount
                            });
                            setShowFinesModal(false);
                        }}
                        onClose={() => setShowFinesModal(false)}
                    />
                )}

                {showOtherChargesModal && (
                    <OtherChargesDetailModal
                        readOnly={!(canAccounts || canOps)}
                        monthName={rows[currentOtherRowIndex]?.start}
                        otherCharges={rows[currentOtherRowIndex]?.other_list || []}
                        legacyAmount={rows[currentOtherRowIndex]?.other || 0}
                        onSave={(newOthers) => {
                            const totalAmount = newOthers.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);
                            handleCellChange(currentOtherRowIndex, {
                                other_list: newOthers,
                                other: totalAmount
                            });
                            setShowOtherChargesModal(false);
                        }}
                        onClose={() => setShowOtherChargesModal(false)}
                    />
                )}

                {showParkingModal && (
                    <ParkingDetailModal
                        readOnly={!canAccounts}
                        monthName={rows[currentParkingRowIndex]?.start}
                        parkingCharges={rows[currentParkingRowIndex]?.parking_list || []}
                        legacyAmount={rows[currentParkingRowIndex]?.parking || 0}
                        onSave={(newParking) => {
                            const totalAmount = newParking.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                            handleCellChange(currentParkingRowIndex, {
                                parking_list: newParking,
                                parking: totalAmount
                            });
                            setShowParkingModal(false);
                        }}
                        onClose={() => setShowParkingModal(false)}
                    />
                )}
                {salikVatModalData && (
                    <SalikVatChoiceModal
                        amount={salikVatModalData.amount}
                        onChoice={(isInclusive) => {
                            const { idx, amount } = salikVatModalData;
                            let finalAmount = amount;
                            if (!isInclusive) {
                                // Add 5% VAT if exclusive
                                finalAmount = parseFloat(withVat(amount).toFixed(2));
                            }

                            const newRows = [...rows];
                            newRows[idx] = {
                                ...newRows[idx],
                                salik: finalAmount,
                                salikHasVat: true
                            };
                            setRows(newRows);
                            setIsDirty(true);
                            setSalikVatModalData(null);
                        }}
                    />
                )}
            </div>
        </Dialog>
    );
};

// Side panel toggle (Addons / Fines / Parking / Other / Rates / Comments)
const SidebarTrigger = ({ icon, label, count, active, selected, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`sidebar-trigger flex-1 min-w-[112px] h-[52px] rounded-lg border flex flex-col items-center justify-center gap-1 ${selected
            ? 'bg-purple-600 border-purple-600 text-white'
            : active ? 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
    >
        <span className="flex items-center gap-1.5 text-xs font-semibold">
            <Icon glyph={icon} size="xs" />
            {label}
        </span>
        <span className={`text-[11px] font-semibold tabular-nums px-2 rounded ${selected ? 'bg-white text-purple-700' : active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
            {count}
        </span>
    </button>
);

// --- Sub-components for Sidebar & Modals ---

const SalikVatChoiceModal = ({ amount, onChoice }) => {
    // A choice is required: no close button, Escape or overlay dismissal (as before).
    return (
        <Dialog
            open
            role="alertdialog"
            title="Salik VAT status"
            size="sm"
            closeOnOverlay={false}
            closeOnEscape={false}
            hideClose
        >
            <p className="text-sm text-gray-600 mb-4">
                Amount: <span className="font-semibold text-gray-900 tabular-nums">{amount.toLocaleString()} AED</span>
            </p>
            <div className="flex flex-col gap-2">
                <Button variant="secondary" fullWidth onClick={() => onChoice(true)} leadingIcon={<Icon glyph={ICONS.check} size="sm" />}>
                    Including VAT
                </Button>
                <Button variant="primary" fullWidth onClick={() => onChoice(false)} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                    Without VAT
                </Button>
            </div>
            <p className="mt-4 text-xs text-gray-500 text-center">Selection required</p>
        </Dialog>
    );
};

const AddonsView = ({ addons, setAddons, setIsDirty, readOnly = false }) => {
    const handleAddonChange = (field, value) => {
        if (readOnly) return;
        setAddons(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const hasActiveAddons = addons.fullInsurance || addons.additionalDriver || (parseFloat(addons.extraKM) > 0);
    const optionCls = (on) => `flex items-center justify-between p-3 rounded-lg border cursor-pointer ${on ? 'bg-white border-green-300' : 'bg-white border-gray-200 hover:border-gray-300'}`;

    return (
        <div className="flex flex-col gap-4">
            <Card>
                <div className="flex items-center gap-3 mb-4">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${hasActiveAddons ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        <Icon glyph={ICONS.star} size="sm" />
                    </div>
                    <div>
                        <h4 className={`text-sm font-semibold ${hasActiveAddons ? 'text-green-800' : 'text-gray-900'}`}>Active Addons</h4>
                        <p className="text-xs text-gray-500">Configure additional services for this contract</p>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className={optionCls(addons.fullInsurance)}>
                        <span className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={addons.fullInsurance || false}
                                disabled={readOnly}
                                onChange={(e) => handleAddonChange('fullInsurance', e.target.checked)}
                                className="w-4 h-4 rounded text-green-600 focus:ring-green-500 border-gray-300"
                            />
                            <span className={`text-sm font-medium ${addons.fullInsurance ? 'text-green-800' : 'text-gray-700'}`}>Full Insurance (CDW)</span>
                        </span>
                        {addons.fullInsurance && <Icon glyph={ICONS.check} size="sm" tone="success" />}
                    </label>

                    <label className={optionCls(addons.additionalDriver)}>
                        <span className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={addons.additionalDriver || false}
                                disabled={readOnly}
                                onChange={(e) => handleAddonChange('additionalDriver', e.target.checked)}
                                className="w-4 h-4 rounded text-green-600 focus:ring-green-500 border-gray-300"
                            />
                            <span className={`text-sm font-medium ${addons.additionalDriver ? 'text-green-800' : 'text-gray-700'}`}>Additional Driver</span>
                        </span>
                        {addons.additionalDriver && <Icon glyph={ICONS.check} size="sm" tone="success" />}
                    </label>

                    <Card padding="sm">
                        <div className="flex items-center justify-between mb-2">
                            <label htmlFor="mc-addon-extra-km" className={`text-sm font-medium ${parseFloat(addons.extraKM) > 0 ? 'text-green-800' : 'text-gray-700'}`}>Extra Kilometers</label>
                            <span className="flex items-center gap-1.5 bg-gray-100 px-2 py-0.5 rounded text-[11px] font-semibold text-gray-600 uppercase">
                                <Icon glyph={ICONS.car} size="xs" /> Limit
                            </span>
                        </div>
                        <div className="relative">
                            <input
                                id="mc-addon-extra-km"
                                type="number"
                                value={addons.extraKM || 0}
                                disabled={readOnly}
                                onChange={(e) => handleAddonChange('extraKM', parseFloat(e.target.value) || 0)}
                                className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 tabular-nums focus:ring-2 focus:ring-purple-200 focus:border-purple-500 outline-none"
                                placeholder="0"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-gray-500 uppercase">KM</span>
                        </div>
                    </Card>
                </div>
            </Card>
        </div>
    );
};

// Shared look for the Fines / Other / Parking side-panel lists
const panelEmptyCls = 'flex flex-col items-center justify-center py-12 text-gray-500 gap-3 text-center';
const PanelGroupTitle = ({ month }) => (
    <span className="flex items-center gap-2">
        <Icon glyph={ICONS.calendar} size="xs" tone="muted" />
        {month}
    </span>
);

const FinesView = ({ rows }) => {
    const allFines = rows.reduce((acc, row) => {
        if (row.fines_list && row.fines_list.length > 0) {
            acc.push({
                month: row.start,
                fines: row.fines_list,
                total: parseFloat(row.fine) || 0
            });
        }
        return acc;
    }, []);

    if (allFines.length === 0) {
        return (
            <div className={panelEmptyCls}>
                <Icon glyph={ICONS.fines} size="lg" tone="muted" />
                <div>
                    <p className="text-sm font-medium text-gray-700">No fines recorded</p>
                    <p className="text-xs mt-1">Traffic fines will appear here once added to any month.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-6">
            {allFines.map((group, idx) => (
                <Card
                    key={idx}
                    padding="none"
                    title={<PanelGroupTitle month={group.month} />}
                    actions={<Badge variant="warning" size="sm">{group.total.toLocaleString()} AED</Badge>}
                >
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>Ticket</TableHeader>
                                    <TableHeader>Amount</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.fines.map((fine, fidx) => (
                                    <TableRow key={fidx}>
                                        <TableCell>
                                            <CellId>{fine.ticketNo || 'N/A'}</CellId>
                                            <CellMuted>{fine.note || 'No description'}</CellMuted>
                                        </TableCell>
                                        <TableCell>
                                            <CellMoney value={parseFloat(fine.amount)} currency="AED" tone="danger" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            ))}

            <Card className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Consolidated Total</div>
                <div className="text-xl font-semibold text-gray-900 tabular-nums">
                    {allFines.reduce((sum, g) => sum + g.total, 0).toLocaleString()} <span className="text-sm text-gray-500">AED</span>
                </div>
            </Card>
        </div>
    );
};

const OtherChargesView = ({ rows }) => {
    const allOthers = rows.reduce((acc, row) => {
        if (row.other_list && row.other_list.length > 0) {
            acc.push({
                month: row.start,
                others: row.other_list,
                total: parseFloat(row.other) || 0
            });
        }
        return acc;
    }, []);

    if (allOthers.length === 0) {
        return (
            <div className={panelEmptyCls}>
                <Icon glyph={ICONS.list} size="lg" tone="muted" />
                <div>
                    <p className="text-sm font-medium text-gray-700">No other charges</p>
                    <p className="text-xs mt-1">Other charges will appear here once added to any month.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-6">
            {allOthers.map((group, idx) => (
                <Card
                    key={idx}
                    padding="none"
                    title={<PanelGroupTitle month={group.month} />}
                    actions={<Badge variant="info" size="sm">{group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })} AED</Badge>}
                >
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>Description</TableHeader>
                                    <TableHeader>Amount</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.others.map((item, oidx) => (
                                    <TableRow key={oidx}>
                                        <TableCell>
                                            <CellText max={180}>{item.title || 'Other Charge'}</CellText>
                                            <CellMuted>{item.note || 'No description'}</CellMuted>
                                        </TableCell>
                                        <TableCell>
                                            <CellMoney value={parseFloat(item.amount)} currency="AED" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            ))}

            <Card className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Consolidated Total</div>
                <div className="text-xl font-semibold text-gray-900 tabular-nums">
                    {allOthers.reduce((sum, g) => sum + g.total, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-sm text-gray-500">AED</span>
                </div>
            </Card>
        </div>
    );
};

const ParkingView = ({ rows }) => {
    const allParking = rows.reduce((acc, row) => {
        if (row.parking_list && row.parking_list.length > 0) {
            acc.push({
                month: row.start,
                parking: row.parking_list,
                total: parseFloat(row.parking) || 0
            });
        }
        return acc;
    }, []);

    if (allParking.length === 0) {
        return (
            <div className={panelEmptyCls}>
                <Icon glyph={ICONS.car} size="lg" tone="muted" />
                <div>
                    <p className="text-sm font-medium text-gray-700">No parking charges</p>
                    <p className="text-xs mt-1">Parking charges will appear here once added to any month.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-6">
            {allParking.map((group, idx) => (
                <Card
                    key={idx}
                    padding="none"
                    title={<PanelGroupTitle month={group.month} />}
                    actions={<Badge variant="accent" size="sm">{group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })} AED</Badge>}
                >
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>Location / Details</TableHeader>
                                    <TableHeader>Amount</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.parking.map((item, pidx) => (
                                    <TableRow key={pidx}>
                                        <TableCell>
                                            <CellText max={180}>{item.location || 'Parking Charge'}</CellText>
                                            <CellMuted>{item.note || 'No description'}</CellMuted>
                                        </TableCell>
                                        <TableCell>
                                            <CellMoney value={parseFloat(item.amount)} currency="AED" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            ))}

            <Card className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Consolidated Total</div>
                <div className="text-xl font-semibold text-gray-900 tabular-nums">
                    {allParking.reduce((sum, g) => sum + g.total, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-sm text-gray-500">AED</span>
                </div>
            </Card>
        </div>
    );
};

const CommentsView = ({
    comments,
    ejarComments = [],
    newCommentText,
    setNewCommentText,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    hasPermission
}) => {
    return (
        <Card
            padding="none"
            className="h-full overflow-hidden"
            title="Internal thread"
            actions={<span className="text-xs text-gray-500 tabular-nums">{comments?.length || 0} Comments{ejarComments.length ? ` · ${ejarComments.length} from Ejar` : ''}</span>}
        >
            <div className="flex flex-col h-full">

            {/* List */}
            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
                {(!comments || comments.length === 0) && ejarComments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-2">
                        <Icon glyph={ICONS.edit} size="lg" tone="muted" />
                        <span className="text-sm">No comments yet</span>
                    </div>
                ) : (
                    comments.map((comment, idx) => (
                        <Card key={idx} padding="sm" className="group relative">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-700 flex items-center justify-center text-sm font-semibold" aria-hidden="true">
                                        {(comment.user || 'U')[0].toUpperCase()}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-gray-900 leading-none">{comment.user || 'Unknown User'}</span>
                                        <span className="text-[11px] text-gray-500 mt-1 tabular-nums">{dubaiDateTime(comment.timestamp)}</span>
                                    </div>
                                </div>

                                {hasPermission(PERMISSIONS.edit_monthly_ops) && hasPermission('monthly_edit_comments') && (
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                                        <IconButton
                                            icon={ICONS.edit}
                                            label="Edit comment"
                                            title="Edit Comment"
                                            size="sm"
                                            onClick={() => handleEditComment(comment.id)}
                                        />
                                        <IconButton
                                            icon={ICONS.close}
                                            label="Delete comment"
                                            title="Delete Comment"
                                            size="sm"
                                            tone="danger"
                                            onClick={() => handleDeleteComment(comment.id)}
                                        />
                                    </div>
                                )}
                            </div>
                            <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap mt-2 border-l-2 border-gray-200 pl-3">{comment.text}</p>
                        </Card>
                    ))
                )}
                {ejarComments.length > 0 && (
                    <div className="flex flex-col gap-3" aria-label="Comments from Ejar">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 pt-1">From Ejar ({ejarComments.length})</div>
                        {ejarComments.map((c) => (
                            <Card key={c.key} padding="sm">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="info">Ejar</Badge>
                                    <span className="text-sm font-semibold text-gray-900 leading-none">{c.by || 'Ejar'}</span>
                                    <span className="text-[11px] text-gray-500 tabular-nums">{c.day}{c.time ? ` ${c.time}` : ''}</span>
                                    {c.type === 'opening' && <Badge variant="neutral">Opening</Badge>}
                                </div>
                                <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap mt-2 border-l-2 border-gray-200 pl-3">{c.text}</p>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Input - Only if has permission */}
            {hasPermission(PERMISSIONS.edit_monthly_ops) && (
                <div className="p-3 bg-gray-50 border-t border-gray-200 mt-auto">
                    <div className="relative">
                        <textarea
                            value={newCommentText}
                            onChange={(e) => setNewCommentText(e.target.value)}
                            placeholder="Type an internal note..."
                            aria-label="New comment"
                            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 pr-12 text-sm text-gray-900 focus:ring-2 focus:ring-purple-200 focus:border-purple-500 outline-none resize-none placeholder:text-gray-500"
                            rows="4"
                        />
                        <Button
                            variant="primary"
                            size="sm"
                            iconOnly
                            aria-label="Add comment"
                            onClick={handleAddComment}
                            disabled={!newCommentText.trim()}
                            className="absolute bottom-3 right-3"
                            leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}
                        />
                    </div>
                </div>
            )}
            </div>
        </Card>
    );
};

// Shared look for the per-month register dialogs (Fines / Parking / Other)
const RegisterSavedList = ({ title = 'Saved List', empty, children }) => (
    <div className="flex-1 min-h-[160px]">
        <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
        {empty ? (
            <div className="h-32 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 gap-1">
                <Icon glyph={ICONS.history} size="md" tone="muted" />
                <span className="text-sm">No entries recorded</span>
            </div>
        ) : (
            <div className="flex flex-col gap-2">{children}</div>
        )}
    </div>
);

const RegisterItem = ({ index, title, meta, note, amount, amountClass = 'text-gray-900', unit, onDelete }) => (
    <Card padding="sm"><div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-gray-100 text-gray-600 rounded-md flex items-center justify-center text-[11px] font-semibold tabular-nums shrink-0">
                #{index}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
                    {meta}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">{note}</div>
            </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
            <div className="text-right tabular-nums">
                <div className={`text-base font-semibold leading-none ${amountClass}`}>{amount}</div>
                <div className="text-[11px] text-gray-500 uppercase mt-1">{unit}</div>
            </div>
            {onDelete && (
                <IconButton
                    icon={ICONS.close}
                    label={`Remove entry ${index}`}
                    size="sm"
                    onClick={onDelete}
                />
            )}
        </div>
    </div></Card>
);

const RegisterFooter = ({ label, total, onClose, onSync }) => (
    <>
        <div className="mr-auto flex flex-col">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide leading-none mb-1">{label}</span>
            <span className="text-lg font-semibold text-gray-900 leading-none tabular-nums">
                {total.toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-xs text-gray-500 ml-1">AED</span>
            </span>
        </div>
        <Button onClick={onClose}>Cancel</Button>
        {onSync && <Button variant="primary" onClick={onSync}>Sync to Month</Button>}
    </>
);

const FineDetailModal = ({ monthName, fines, allExistingTickets, onSave, onClose, readOnly = false }) => {
    const [localFines, setLocalFines] = useState([...fines]);
    const [newFine, setNewFine] = useState({ amount: '', ticketNo: '', note: '', date: '' });
    const [errorMsg, setErrorMsg] = useState("");

    const isDuplicate = (ticket) => {
        if (!ticket) return false;
        const normalized = String(ticket).trim().toLowerCase();
        // Check local fines (current session)
        if (localFines.some(f => String(f.ticketNo).trim().toLowerCase() === normalized)) return true;
        // Check global tickets (other months)
        if (allExistingTickets.includes(normalized)) return true;
        return false;
    };

    const handleAddFine = () => {
        if (!newFine.amount) return;
        if (newFine.ticketNo && isDuplicate(newFine.ticketNo)) {
            setErrorMsg("This Ticket Number already exists!");
            setTimeout(() => setErrorMsg(""), 3000);
            return;
        }
        setLocalFines([...localFines, { ...newFine, id: Date.now() }]);
        setNewFine({ amount: '', ticketNo: '', note: '', date: '' });
        setErrorMsg("");
    };

    const handleDeleteFine = (id) => {
        setLocalFines(localFines.filter(f => (f.id || f.ticketNo) !== id));
    };

    const total = localFines.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);

    const handleSync = () => {
        let finalFines = [...localFines];
        if (newFine.amount) {
            if (newFine.ticketNo && isDuplicate(newFine.ticketNo)) {
                setErrorMsg("This Ticket Number already exists!");
                // Keep the modal open so user can fix the duplicate
                return;
            }
            finalFines.push({ ...newFine, id: Date.now() });
        }
        onSave(finalFines);
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Traffic fines register"
            description={`Month entry: ${monthName || ''}`}
            size="lg"
            closeOnOverlay={false}
            footer={<RegisterFooter label="Row total balance" total={total} onClose={onClose} onSync={readOnly ? undefined : handleSync} />}
        >
            <div className="flex flex-col gap-5">
                {/* Add New Fine Form */}
                {!readOnly && (
                    <FormSection title="Add entry">
                        {errorMsg && <div className="text-sm text-red-700 mb-2" role="alert">{errorMsg}</div>}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                            <Field label="Amount">
                                <Input
                                    type="number"
                                    placeholder="0.00"
                                    value={newFine.amount}
                                    onChange={e => setNewFine({ ...newFine, amount: e.target.value })}
                                />
                            </Field>
                            <Field label="Ticket No." error={isDuplicate(newFine.ticketNo) ? 'Already exists' : undefined}>
                                <Input
                                    type="text"
                                    placeholder="e.g. 12345"
                                    value={newFine.ticketNo}
                                    onChange={e => setNewFine({ ...newFine, ticketNo: e.target.value })}
                                />
                            </Field>
                            <Field label="Date">
                                <Input
                                    type="text"
                                    placeholder="DD/MM/YYYY"
                                    value={newFine.date}
                                    onChange={e => setNewFine({ ...newFine, date: e.target.value })}
                                />
                            </Field>
                            <Button onClick={handleAddFine} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                Add Fine
                            </Button>
                            <div className="col-span-2 sm:col-span-4">
                                <Field label="Reason / Note">
                                    <Input
                                        type="text"
                                        placeholder="Enter fine reason or location..."
                                        value={newFine.note}
                                        onChange={e => setNewFine({ ...newFine, note: e.target.value })}
                                    />
                                </Field>
                            </div>
                        </div>
                    </FormSection>
                )}

                {/* Fines List */}
                <RegisterSavedList empty={localFines.length === 0}>
                    {localFines.map((fine, idx) => (
                        <RegisterItem
                            key={idx}
                            index={idx + 1}
                            title={fine.ticketNo}
                            meta={<span className="text-[11px] text-gray-600 px-1.5 py-0.5 bg-gray-100 rounded tabular-nums">{fine.date || 'No Date'}</span>}
                            note={fine.note || 'No description provided'}
                            amount={parseFloat(fine.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            amountClass="text-red-700"
                            unit="AED"
                            onDelete={readOnly ? undefined : () => handleDeleteFine(fine.id || fine.ticketNo)}
                        />
                    ))}
                </RegisterSavedList>
            </div>
        </Dialog>
    );
};


const ParkingDetailModal = ({ monthName, parkingCharges, legacyAmount, onSave, onClose, readOnly = false }) => {
    // If no specific details but a legacy 'parking' amount exists, we seed it with that amount
    const initialCharges = (parkingCharges.length === 0 && legacyAmount > 0)
        ? [{ amount: legacyAmount, location: 'Previous Charge', note: 'Autogenerated from past data', id: Date.now() }]
        : [...parkingCharges];

    const [localParking, setLocalParking] = useState(initialCharges);
    const [newParking, setNewParking] = useState({ amount: '', location: '', note: '', isVatInclusive: false });

    const handleAddParking = () => {
        if (!newParking.amount) return;

        let finalAmount = parseFloat(newParking.amount) || 0;
        if (!newParking.isVatInclusive) {
            finalAmount = parseFloat(withVat(finalAmount).toFixed(2));
        }

        setLocalParking([...localParking, { ...newParking, amount: finalAmount, id: Date.now() }]);
        setNewParking({ amount: '', location: '', note: '', isVatInclusive: false });
    };

    const handleDeleteParking = (id) => {
        setLocalParking(localParking.filter(p => p.id !== id));
    };

    const total = localParking.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const handleSync = () => {
        let finalParking = [...localParking];
        if (newParking.amount) {
            let finalAmount = parseFloat(newParking.amount) || 0;
            if (!newParking.isVatInclusive) {
                finalAmount = parseFloat(withVat(finalAmount).toFixed(2));
            }
            finalParking.push({ ...newParking, amount: finalAmount, id: Date.now() });
        }
        onSave(finalParking);
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Parking register"
            description={`Month entry: ${monthName || ''}`}
            size="lg"
            closeOnOverlay={false}
            footer={<RegisterFooter label="Row total (inc VAT)" total={total} onClose={onClose} onSync={readOnly ? undefined : handleSync} />}
        >
            <div className="flex flex-col gap-5">
                {/* Add New Parking Form */}
                {!readOnly && (
                    <FormSection title="Add entry">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                            <Field label="Location / Details">
                                <Input
                                    type="text"
                                    placeholder="e.g. Mall Parking"
                                    value={newParking.location}
                                    onChange={e => setNewParking({ ...newParking, location: e.target.value })}
                                />
                            </Field>
                            <Field label="Amount">
                                <Input
                                    type="number"
                                    placeholder="0.00"
                                    value={newParking.amount}
                                    onChange={e => setNewParking({ ...newParking, amount: e.target.value })}
                                />
                            </Field>
                            <div className="col-span-2 flex items-end pb-2">
                                <Checkbox
                                    label="Already VAT Inclusive"
                                    checked={newParking.isVatInclusive}
                                    onChange={e => setNewParking({ ...newParking, isVatInclusive: e.target.checked })}
                                />
                            </div>
                            <div className="col-span-2">
                                <Field label="Notes">
                                    <Input
                                        type="text"
                                        placeholder="Optional notes..."
                                        value={newParking.note}
                                        onChange={e => setNewParking({ ...newParking, note: e.target.value })}
                                    />
                                </Field>
                            </div>
                            <div className="col-span-2 flex justify-end">
                                <Button onClick={handleAddParking} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                    Add Parking
                                </Button>
                            </div>
                        </div>
                    </FormSection>
                )}

                {/* Parking List */}
                <RegisterSavedList empty={localParking.length === 0}>
                    {localParking.map((parking, idx) => (
                        <RegisterItem
                            key={idx}
                            index={idx + 1}
                            title={parking.location || 'Parking Charge'}
                            note={parking.note || 'No description provided'}
                            amount={parseFloat(parking.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            unit="AED (Net)"
                            onDelete={readOnly ? undefined : () => handleDeleteParking(parking.id)}
                        />
                    ))}
                </RegisterSavedList>
            </div>
        </Dialog>
    );
};

const OtherChargesDetailModal = ({ monthName, otherCharges, legacyAmount, onSave, onClose, readOnly = false }) => {
    // If no specific details but a legacy 'other' amount exists, we seed it with that amount
    const initialCharges = (otherCharges.length === 0 && legacyAmount > 0)
        ? [{ amount: legacyAmount, title: 'Previous Charge', note: 'Autogenerated from past data', id: Date.now() }]
        : [...otherCharges];

    const [localOthers, setLocalOthers] = useState(initialCharges);
    const [newOther, setNewOther] = useState({ amount: '', title: '', note: '', isVatInclusive: false });

    const handleAddOther = () => {
        if (!newOther.amount) return;

        let finalAmount = parseFloat(newOther.amount) || 0;
        if (!newOther.isVatInclusive) {
            finalAmount = parseFloat(withVat(finalAmount).toFixed(2));
        }

        setLocalOthers([...localOthers, { ...newOther, amount: finalAmount, id: Date.now() }]);
        setNewOther({ amount: '', title: '', note: '', isVatInclusive: false });
    };

    const handleDeleteOther = (id) => {
        setLocalOthers(localOthers.filter(o => o.id !== id));
    };

    const total = localOthers.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);

    const handleSync = () => {
        let finalOthers = [...localOthers];
        if (newOther.amount) {
            let finalAmount = parseFloat(newOther.amount) || 0;
            if (!newOther.isVatInclusive) {
                finalAmount = parseFloat(withVat(finalAmount).toFixed(2));
            }
            finalOthers.push({ ...newOther, amount: finalAmount, id: Date.now() });
        }
        onSave(finalOthers);
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Other charges"
            description={`Month entry: ${monthName || ''}`}
            size="lg"
            closeOnOverlay={false}
            footer={<RegisterFooter label="Row total (inc VAT)" total={total} onClose={onClose} onSync={readOnly ? undefined : handleSync} />}
        >
            <div className="flex flex-col gap-5">
                {/* Add New Other Form */}
                {!readOnly && (
                    <FormSection title="Add new charge">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                            <Field label="Title">
                                <Input
                                    type="text"
                                    placeholder="e.g. Wash"
                                    value={newOther.title}
                                    onChange={e => setNewOther({ ...newOther, title: e.target.value })}
                                />
                            </Field>
                            <Field label="Amount">
                                <Input
                                    type="number"
                                    placeholder="0.00"
                                    value={newOther.amount}
                                    onChange={e => setNewOther({ ...newOther, amount: e.target.value })}
                                />
                            </Field>
                            <div className="col-span-2 flex items-end pb-2">
                                <Checkbox
                                    label="Already VAT Inclusive"
                                    checked={newOther.isVatInclusive}
                                    onChange={e => setNewOther({ ...newOther, isVatInclusive: e.target.checked })}
                                />
                            </div>
                            <div className="col-span-2">
                                <Field label="Notes / Details">
                                    <Input
                                        type="text"
                                        placeholder="Further description..."
                                        value={newOther.note}
                                        onChange={e => setNewOther({ ...newOther, note: e.target.value })}
                                    />
                                </Field>
                            </div>
                            <div className="col-span-2 flex justify-end">
                                <Button onClick={handleAddOther} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                    Add Charge
                                </Button>
                            </div>
                        </div>
                    </FormSection>
                )}

                {/* Charges List */}
                <RegisterSavedList empty={localOthers.length === 0}>
                    {localOthers.map((other, idx) => (
                        <RegisterItem
                            key={idx}
                            index={idx + 1}
                            title={other.title || 'Other Charge'}
                            note={other.note || 'No comment provided'}
                            amount={parseFloat(other.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            unit="AED (Net)"
                            onDelete={readOnly ? undefined : () => handleDeleteOther(other.id)}
                        />
                    ))}
                </RegisterSavedList>
            </div>
        </Dialog>
    );
};

// Helper function to draw Rent Invoice Page
const drawRentInvoicePage = (doc, row, contract, invoiceDate, trnNumber, imgData, companyName, companyLocation, companyTRN) => {
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Add background image
    doc.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);

    // Set default font
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    let y = 30; // Start Y position

    // Header: Tax Invoice
    doc.setFontSize(26);
    doc.setFont('helvetica', 'bold');
    doc.text('Tax Invoice', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // Date and Ref
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Date: ${invoiceDate}`, margin, y);

    const refText = `Ref: ${row.invNo || ''}`;
    const refWidth = doc.getTextWidth(refText);
    doc.text(refText, pageWidth - margin - refWidth, y);
    y += 5;

    // TRN
    doc.text(`TRN#: ${trnNumber}`, margin, y);
    y += 5;

    // Address
    const drawDynamicText = (text, x, currentY, size, isBold = false) => {
        const img = getArabicTextImg(text, size, isBold);
        if (img) {
            // Adjust X for left margin
            const imgW = img.width * (0.264583); // px to mm approx
            const imgH = img.height * (0.264583);
            doc.addImage(img.data, 'PNG', x, currentY - (imgH / 2), imgW, imgH);
            return currentY + 7;
        }
        return currentY;
    };

    if (companyName) {
        y = drawDynamicText(companyName, margin - 2, y, 12, true);
    }
    if (companyLocation) {
        y = drawDynamicText(companyLocation, margin - 2, y, 12, true);
    }
    if (companyTRN) {
        doc.setFont('helvetica', 'bold');
        doc.text(`TRN: ${companyTRN}`, margin, y);
        y += 5;
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Dubai, U.A.E.', margin, y);
    y += 8;

    // Subject
    doc.text('SUB: Monthly Rental', margin, y);
    y += 10;

    // Greeting
    doc.text('Dear Sir,', margin, y);
    y += 5;
    doc.text('We thank you for your renting the below vehicle.', margin, y);
    y += 10;

    // --- Customer & Vehicle Info Section ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Customer Details:', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    
    const infoLines = [
        `Name: ${contract.customer || ''} | R/A: ${contract.contractNo || ''}`,
        // the plate's own model: a swapped contract's Ejar model is the car it has now (4 Nov 2026), not its own car's
        `Vehicle: ${(contract.car_now_at && contract.invygoPlate && contract.invygoModel) || contract.model || contract.Model || ''} - ${contract.invygoPlate || contract.ejarPlate || contract.plateNo || ''} | Period: ${row.start} to ${row.end}`
    ];
    
    infoLines.forEach(line => {
        y = drawDynamicText(line, margin, y, 11, true);
    });
    y += 5;

    // --- Table Setup ---
    const colNoW = 10;
    const colVatW = 30;
    const colTotalW = 40;
    const colDescW = contentWidth - colNoW - colVatW - colTotalW;

    const colX = [margin, margin + colNoW, margin + colNoW + colDescW, margin + colNoW + colDescW + colVatW];

    // Draw Header Background
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y - 5, contentWidth, 10, 'F');

    // Header Text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('No.', colX[0] + (colNoW / 2), y, { align: 'center' });
    doc.text('Description', colX[1] + (colDescW / 2), y, { align: 'center' });
    doc.text('VAT (5%)', colX[2] + (colVatW / 2), y, { align: 'center' });
    doc.text('Total Price', colX[3] + (colTotalW / 2), y, { align: 'center' });

    // Header Border
    doc.rect(margin, y - 5, contentWidth, 10, 'S');
    doc.line(colX[1], y - 5, colX[1], y + 5);
    doc.line(colX[2], y - 5, colX[2], y + 5);
    doc.line(colX[3], y - 5, colX[3], y + 5);

    y += 5;

    // --- Row Data Logic ---
    const tableRows = [];
    
    // 1. Rent
    if (parseFloat(row.rent) > 0 || (parseFloat(row.salik) === 0 && parseFloat(row.parking) === 0 && parseFloat(row.fine) === 0)) {
        const rentBase = parseFloat(row.rent) || 0;
        tableRows.push({
            desc: `Monthly Rental: ${rentBase.toFixed(2)} AED - Period: ${row.start} to ${row.end}`,
            vat: parseFloat(row.vat) || 0,
            total: (parseFloat(row.rent) || 0) + (parseFloat(row.vat) || 0)
        });
    }

    // 2. Salik
    if (parseFloat(row.salik) > 0) {
        const amt = parseFloat(row.salik);
        if (row.salikHasVat) {
            const baseSalik = parseFloat(withoutVat(amt).toFixed(2));
            const vatSalik = parseFloat((amt - baseSalik).toFixed(2));
            tableRows.push({
                desc: `Salik Charges: ${baseSalik.toFixed(2)} AED`,
                vat: vatSalik,
                total: amt
            });
        } else {
            tableRows.push({
                desc: `Salik Charges: ${amt.toFixed(2)} AED`,
                vat: 0,
                total: amt
            });
        }
    }

    // 3. Parking
    if (parseFloat(row.parking) > 0) {
        const totalParking = parseFloat(row.parking);
        const baseParking = parseFloat(withoutVat(totalParking).toFixed(2));
        const vatParking = parseFloat((totalParking - baseParking).toFixed(2));
        tableRows.push({
            desc: `Parking Charges: ${baseParking.toFixed(2)} AED`,
            vat: vatParking,
            total: totalParking
        });
    }

    // 4. Traffic Fines
    if (parseFloat(row.fine) > 0) {
        const amt = parseFloat(row.fine);
        tableRows.push({
            desc: `Traffic Fine Charges: ${amt.toFixed(2)} AED`,
            vat: 0,
            total: amt
        });
    }

    // 5. Other Charges (Back-calculate VAT)
    if (parseFloat(row.other) > 0) {
        const totalOther = parseFloat(row.other);
        const baseOther = parseFloat(withoutVat(totalOther).toFixed(2));
        const vatOther = parseFloat((totalOther - baseOther).toFixed(2));
        tableRows.push({
            desc: `Other Charges: ${baseOther.toFixed(2)} AED`,
            vat: vatOther,
            total: totalOther
        });
    }

    let totalVat = 0;
    let totalAmount = 0;

    tableRows.forEach((item, index) => {
        const rowH = 10;
        const rowY = y;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        
        // No
        doc.text((index + 1).toString(), colX[0] + (colNoW / 2), rowY + 6.5, { align: 'center' });
        
        // Description
        drawDynamicText(item.desc, colX[1] + 3, rowY + 6.5, 10, true);
        
        // VAT
        doc.text(item.vat.toFixed(2), colX[2] + (colVatW / 2), rowY + 6.5, { align: 'center' });
        
        // Total
        doc.setFont('helvetica', 'bold');
        doc.text(item.total.toFixed(2), colX[3] + colTotalW - 2, rowY + 6.5, { align: 'right' });
        
        // Borders
        doc.rect(margin, rowY, contentWidth, rowH, 'S');
        doc.line(colX[1], rowY, colX[1], rowY + rowH);
        doc.line(colX[2], rowY, colX[2], rowY + rowH);
        doc.line(colX[3], rowY, colX[3], rowY + rowH);
        
        totalVat += item.vat;
        totalAmount += item.total;
        y += rowH;
    });

    // TOTAL ROW
    doc.setFillColor(220, 220, 220);
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Grand Total:', colX[2] - 2, y + 6.5, { align: 'right' });
    doc.text(totalVat.toFixed(2), colX[2] + (colVatW / 2), y + 6.5, { align: 'center' });
    doc.text(`AED ${totalAmount.toFixed(2)}`, colX[3] + colTotalW - 2, y + 6.5, { align: 'right' });
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'S');
    doc.line(colX[3], y, colX[3], y + 10);
    y += 10;

    // PAID AMOUNT ROW
    const paidAmt = parseFloat(row.paid) || 0;
    doc.setFillColor(245, 245, 245);
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Paid Amount:', colX[2] - 2, y + 6.5, { align: 'right' });
    doc.setTextColor(0, 150, 0); // Green color
    doc.text(`AED ${paidAmt.toFixed(2)}`, colX[3] + colTotalW - 2, y + 6.5, { align: 'right' });
    doc.setTextColor(0, 0, 0); // Reset to black
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'S');
    doc.line(colX[3], y, colX[3], y + 10);
    y += 10;

    // NET BALANCE ROW
    const balance = calculateBalance(row);
    doc.setFillColor(235, 235, 235);
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Net Balance:', colX[2] - 2, y + 6.5, { align: 'right' });
    doc.setTextColor(balance > 0 ? 200 : 0, 0, 0); // Red-ish if balance exists
    doc.text(`AED ${balance.toFixed(2)}`, colX[3] + colTotalW - 2, y + 6.5, { align: 'right' });
    doc.setTextColor(0, 0, 0); // Reset to black
    doc.rect(colX[2], y, colVatW + colTotalW, 10, 'S');
    doc.line(colX[3], y, colX[3], y + 10);

    y += 20;

    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('General Conditions:', margin, y);
    doc.line(margin, y + 1, margin + 35, y + 1);
    y += 10;
    doc.text('Terms of Payment : within 7 days', margin, y);
    y += 15;
    doc.text('Thanking you and assuring you of our best co-operation and services at all times.', margin, y);
    y += 15;
    doc.text('Best Regards,', margin, y);
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Saudian Alwefaq Rent A Car', margin, y);
};

export default MonthlyCalculator;
